/** 诉讼台账方案：标准字段、模板识别与导出列映射。 */
import {LITIGATION_CREATOR_UNIT_ID} from './litigationCollaborationAccess.js';

import {
  alignLitigationCourtNumberRows,
  alignProcedureNodesForStage,
  applyStageToNodes,
  currentStageName,
  DISPUTE_TYPE_LABELS,
  emptyLitigationCourtNumbers,
  INSTANCE_LABELS,
  latestLitigationProgress,
  litigationCourtNumberLayersForDispute,
  litigationCourtNumberSummary,
  litigationInstanceFromCourtRows,
  splitLitigationCourtNumberCell,
  litigationVenueSummary,
  splitLitigationVenueCell,
  nextDeadlineDate,
  normalizeLitigationCourtNumberRows,
  normalizeLitigationCourtNumbers,
  POSITION_LABELS,
  partyNameByRole,
  formatPartyLayerCell,
  litigationPartyLayersForCase,
  parsePartyLayerCell,
  replacePartyRoleLayers,
  procedureTemplateFor,
  ourPartyName,
  resolveLitigationVenueRows,
  upsertPartyRoleName,
  CLOSE_MODE_LABELS,
  ENFORCEMENT_STATUS_LABELS,
  type LitigationCase,
  type LitigationCaseNature,
  type LitigationCourtNumberLayer,
  type LitigationCourtNumberRow,
  type LitigationCourtNumbers,
  type LitigationDisputeType,
  type LitigationInstance,
  type LitigationPartyLayer,
  type LitigationPosition,
  type LitigationProcedureNode,
} from './litigationLedger.js';
export {LITIGATION_COURT_NUMBER_LAYERS} from './litigationLedger.js';
import {parseCloseMode, type LitigationCloseMode, type LitigationEnforcementStatus} from './litigationLedgerRules.js';
import {
  LITIGATION_DETAIL_FIELDS,
  LITIGATION_DEFAULT_SHEETS,
  defaultLitigationSheetMaps,
  detectLitigationSheetKind,
  litigationFieldsForSheetKind,
  type LitigationSheetKind,
} from './litigationLedgerSheets.js';

export type LitigationPlanStandardField = {
  id: string;
  label: string;
  aliases: string[];
  group: 'basic' | 'party' | 'procedure' | 'result' | 'meta';
  listDefault?: boolean;
  sheetKind?: 'cases' | 'parties' | 'procedure' | 'progress';
};

/** 标准字段在 Excel 中的填写规则。 */
export type LitigationFieldFillGuide = {
  rule: string;
  example?: string;
  allowed?: string;
};

export type LitigationPlanFieldMapping = {
  enabled: boolean;
  columnIndex: number;
  columnLetter: string;
  header: string;
  sourceField: string;
  sourceModuleId?: string;
  sourceModuleVersion?: string;
  valueMode?: 'source' | 'formula' | 'reference';
  valueTransform?: '' | 'left' | 'right' | 'prefix' | 'suffix';
  transformLength?: number;
  transformText?: string;
  calculationExpression?: string;
  calculationDecimalPlaces?: number;
  calculationMissingValuePolicy?: 'error' | 'zero';
  referenceSheetName?: string;
  referenceColumnIndex?: number;
  referenceColumnLetter?: string;
  referenceHeader?: string;
};

/** 内部案号可选用的组成段。 */
export type LitigationInternalNoPartKind = 'year' | 'month' | 'day' | 'orgCode' | 'caseType' | 'serial' | 'other';

/** 编号用案件类型。执行按独立类型取号，不新增争议类型。 */
export type LitigationNumberingKind = 'ss' | 'zx' | 'zc' | 'ldzc';

export type LitigationInternalNoPart = {
  kind: LitigationInternalNoPartKind;
  enabled: boolean;
};

export type LitigationInternalNoRule = {
  parts: LitigationInternalNoPart[];
  separator: string;
  serialWidth: number;
  orgCode: string;
  otherText: string;
  caseTypeCodes: Record<LitigationNumberingKind, string>;
  note: string;
};

/** 生成内部案号时用到的立案信息。 */
export type LitigationInternalNoContext = {
  filingDate?: string;
  numberingKind?: string;
  disputeType?: string;
  instance?: string;
  enforcementStatus?: string;
  /**
   * 所属单位代号。传入时用这个值，不再用规则里手写的代号。
   * 方案管理员用所属单位代码，协作人员用所属协作单位的代码。
   */
  orgCode?: string;
};

export const LITIGATION_INTERNAL_NO_MAX_LENGTH = 80;
export const LITIGATION_INTERNAL_NO_PART_KINDS: readonly LitigationInternalNoPartKind[] = [
  'year', 'month', 'day', 'orgCode', 'caseType', 'serial', 'other',
];
export const LITIGATION_INTERNAL_NO_PART_LABELS: Record<LitigationInternalNoPartKind, string> = {
  year: '年份',
  month: '月',
  day: '日',
  orgCode: '所属单位代号',
  caseType: '案件类型',
  serial: '编号',
  other: '其他',
};

/** 台账「所属单位」列的显示写法。只由方案创建者选定。 */
export type LitigationOrganizationLabelKind = 'name' | 'shortName' | 'code';

/** 所属单位显示写法的界面文案。 */
export const LITIGATION_ORGANIZATION_LABEL_KIND_LABELS: Record<LitigationOrganizationLabelKind, string> = {
  name: '全称',
  shortName: '简称',
  code: '代码',
};

/**
 * 把方案里保存的选项解析成所属单位显示写法。无法识别时用全称。
 * @param raw 方案保存值或界面选项
 */
export function parseLitigationOrganizationLabelKind(raw: string): LitigationOrganizationLabelKind {
  const key = String(raw || '').trim();
  if (key === 'shortName' || key === '简称') return 'shortName';
  if (key === 'code' || key === '代码') return 'code';
  return 'name';
}
export const LITIGATION_NUMBERING_KIND_LABELS: Record<LitigationNumberingKind, string> = {
  ss: '诉讼',
  zx: '执行',
  zc: '仲裁',
  ldzc: '劳动仲裁',
};

/** 案件类型为诉讼时，「当前阶段」的固定选项。一审、二审、再审分开列出。 */
export const LITIGATION_TRIAL_STAGE_OPTIONS = [
  '立案受理', '举证', '开庭', '裁判/调解',
  '上诉', '二审举证', '二审开庭', '二审裁判',
  '再审立案', '再审举证', '再审开庭', '再审裁判',
  '执行', '已结案',
] as const;

/** 案件类型为仲裁时，「当前阶段」的固定选项。 */
export const ARBITRATION_TRIAL_STAGE_OPTIONS = ['申请/答辩', '组庭', '开庭', '裁决', '执行', '已结案'] as const;

/** 案件类型为执行时，「当前阶段」的固定选项。 */
export const ENFORCEMENT_TRIAL_STAGE_OPTIONS = ['立案受理', '财产查控', '财产处置', '案款发放', '执行和解', '已结案'] as const;

/** 诉讼、仲裁案件的结案方式。 */
export const LITIGATION_CLOSE_MODE_IDS = ['judgment', 'mediation', 'withdrawal', 'ruling'] as const satisfies readonly Exclude<LitigationCloseMode, ''>[];

/** 执行案件的结案方式。 */
export const ENFORCEMENT_CLOSE_MODE_IDS = ['enforcement_completed', 'enforcement_suspension', 'enforcement_termination', 'enforcement_cancellation', 'enforcement_rejection', 'enforcement_refusal'] as const satisfies readonly Exclude<LitigationCloseMode, ''>[];

/**
 * 诉讼、仲裁或执行案件的当前阶段选项。劳动仲裁仍沿用原有节点。
 * @param item 案件类型相关字段
 */
export function litigationTrialStageOptions(item: Pick<LitigationCase, 'disputeType' | 'instance' | 'enforcementStatus'>): readonly string[] {
  const kind = deriveLitigationNumberingKind({
    disputeType: item.disputeType,
    instance: item.instance,
    enforcementStatus: item.enforcementStatus,
  });
  if (kind === 'ss') return LITIGATION_TRIAL_STAGE_OPTIONS;
  if (kind === 'zc') return ARBITRATION_TRIAL_STAGE_OPTIONS;
  if (kind === 'zx') return ENFORCEMENT_TRIAL_STAGE_OPTIONS;
  return [];
}

/**
 * 用户提交的当前阶段能否保存。
 * 空值表示清空。诉讼、仲裁、执行认各自的固定阶段；劳动仲裁认程序节点名。不拿「执行中」「未立案」这类推算进度名来挡保存。
 * @param value 用户选择的阶段
 * @param item 案件类型相关字段
 */
export function litigationCurrentStageChoiceAccepted(
  value: string,
  item: Pick<LitigationCase, 'disputeType' | 'instance' | 'enforcementStatus'>,
): boolean {
  const text = value.trim();
  if (!text || text === '已结案') return true;
  if (litigationTrialStageOptions(item).includes(text)) return true;
  return procedureTemplateFor(item.disputeType).some((node) => node.name === text);
}

/**
 * 按案件类型给出结案方式。执行案件不用判决、调解、撤诉、裁定。
 * @param item 案件类型相关字段
 */
export function litigationCloseModeOptions(item: Pick<LitigationCase, 'disputeType' | 'instance' | 'enforcementStatus'>): readonly string[] {
  const ids = deriveLitigationNumberingKind(item) === 'zx' ? ENFORCEMENT_CLOSE_MODE_IDS : LITIGATION_CLOSE_MODE_IDS;
  return ids.map((id) => CLOSE_MODE_LABELS[id]);
}

/**
 * 结案方式的代码与名称，供表单下拉使用。
 * @param item 案件类型相关字段
 */
export function litigationCloseModeChoices(item: Pick<LitigationCase, 'disputeType' | 'instance' | 'enforcementStatus'>): Array<{id: Exclude<LitigationCloseMode, ''>; label: string}> {
  const ids = deriveLitigationNumberingKind(item) === 'zx' ? ENFORCEMENT_CLOSE_MODE_IDS : LITIGATION_CLOSE_MODE_IDS;
  return ids.map((id) => ({id, label: CLOSE_MODE_LABELS[id]}));
}
const DEFAULT_CASE_TYPE_CODES: Record<LitigationNumberingKind, string> = {
  ss: 'ss',
  zx: 'zx',
  zc: 'zc',
  ldzc: 'ldzc',
};

/** 团队方案中的协作单位。 */
export type LitigationPlanCollaborationUnit = {
  id: string;
  name: string;
  shortName: string;
  /** 方案管理员为该单位填写的代码。 */
  code: string;
};

/** 团队方案下，除创建者外默认参与编辑诉讼台账的成员。 */
export type LitigationPlanTeamEditor = {
  userId: string;
  displayName: string;
  /** 所属协作单位 ID。 */
  unitId: string;
};

/**
 * 规范化协作单位与协作人员，并把旧数据里写在人员上的单位拆成单位列表。
 * @param input 原始单位与人员
 * @param fallback 现有数据
 */
export function normalizeLitigationPlanCollaboration(
  input: {units?: unknown; editors?: unknown},
  fallback: {units?: LitigationPlanCollaborationUnit[]; editors?: LitigationPlanTeamEditor[]} = {},
): {units: LitigationPlanCollaborationUnit[]; editors: LitigationPlanTeamEditor[]} {
  const rawUnits = Array.isArray(input.units) ? input.units : (fallback.units ?? []);
  const units: LitigationPlanCollaborationUnit[] = [];
  const seenUnit = new Set<string>();
  rawUnits.forEach((entry, index) => {
    const record = entry && typeof entry === 'object' ? entry as Record<string, unknown> : {};
    const name = String(record.name ?? '').trim().slice(0, 200);
    const shortName = String(record.shortName ?? '').trim().slice(0, 40);
    const code = String(record.code ?? '').trim().slice(0, 40);
    const id = String(record.id ?? '').trim().slice(0, 80) || `unit_${index + 1}`;
    if (id === LITIGATION_CREATOR_UNIT_ID) throw new Error('管理员所属单位不能作为普通协作单位重复添加');
    if (!id || seenUnit.has(id)) return;
    seenUnit.add(id);
    units.push({id, name, shortName, code});
  });
  const rawEditors = Array.isArray(input.editors) ? input.editors : (fallback.editors ?? []);
  const editors: LitigationPlanTeamEditor[] = [];
  const seenUser = new Set<string>();
  for (const entry of rawEditors) {
    const record = entry && typeof entry === 'object' ? entry as Record<string, unknown> : {};
    const userId = String(record.userId ?? '').trim().slice(0, 80);
    if (!userId || seenUser.has(userId)) continue;
    seenUser.add(userId);
    let unitId = String(record.unitId ?? '').trim().slice(0, 80);
    const legacyName = String(record.organizationName ?? '').trim().slice(0, 200);
    const legacyShort = String(record.organizationShortName ?? '').trim().slice(0, 40);
    if (!unitId && legacyName) {
      const existing = units.find((item) => item.name === legacyName && item.shortName === legacyShort);
      if (existing) unitId = existing.id;
      else {
        unitId = `unit_legacy_${units.length + 1}`;
        units.push({id: unitId, name: legacyName, shortName: legacyShort, code: ''});
      }
    }
    editors.push({
      userId,
      displayName: String(record.displayName ?? '').trim().slice(0, 160),
      unitId,
    });
  }
  return {units, editors};
}

/**
 * 规范化团队方案参与编辑成员列表。
 * @param value 原始输入
 * @param fallback 现有列表
 */
export function normalizeLitigationPlanTeamEditors(
  value: unknown,
  fallback: LitigationPlanTeamEditor[] = [],
): LitigationPlanTeamEditor[] {
  return normalizeLitigationPlanCollaboration({editors: value}, {editors: fallback}).editors;
}

export type LitigationLedgerPlan = {
  id: string;
  name: string;
  companyName: string;
  templateFileId: string;
  fileName: string;
  sheetName: string;
  headerRow: number;
  dataStartRow: number;
  listFieldIds: string[];
  exportFields: LitigationPlanFieldMapping[];
  sheetMaps?: Array<{
    sheetKind: 'cases' | 'parties' | 'procedure' | 'progress';
    sheetName: string;
    headerRow: number;
    dataStartRow: number;
    exportFields: LitigationPlanFieldMapping[];
  }>;
  internalNoRule: LitigationInternalNoRule;
  /** 平台下发的字段填写规则覆盖（后台可改，客户端表单据此决定只读与提示）。 */
  fieldRuleOverrides?: LitigationFieldRuleOverrides;
  procedureTemplates: Partial<Record<LitigationDisputeType, Array<Pick<LitigationProcedureNode, 'id' | 'name' | 'keyDeadline' | 'deadlineKind'>>>>;
  /** 个人或团队方案（团队方案写入团队空间「诉讼台账」目录）。 */
  planScope?: 'personal' | 'enterprise';
  /** 创建方案的用户 ID（团队方案下用于控制创建者信息可见性）。 */
  createdBy?: string;
  /** 创建者姓名（仅创建者可查看与修改）。 */
  creatorDisplayName?: string;
  /** 创建者所属单位名称（仅创建者可查看与修改）。 */
  creatorOrganizationName?: string;
  /** 创建者所属单位简称（仅创建者可查看与修改）。 */
  creatorOrganizationShortName?: string;
  /** 创建者所属单位代码（仅创建者可查看与修改）。 */
  creatorOrganizationCode?: string;
  /**
   * 台账「所属单位」列显示哪一种。只由方案创建者选定。
   * 内部案号仍只用单位代码，不随这个选项变化。
   */
  organizationLabelKind?: LitigationOrganizationLabelKind;
  /** 团队方案：协作单位名称与简称（仅创建者可配置）。 */
  collaborationUnits?: LitigationPlanCollaborationUnit[];
  /** 团队方案：默认参与编辑诉讼台账的其他成员及其所属协作单位（仅创建者可配置）。 */
  teamEditors?: LitigationPlanTeamEditor[];
  createdAt: string;
  updatedAt: string;
};

/** 方案映射里标注为「诉讼台账」、由系统回写/同步的标准字段。 */
export const LITIGATION_PLAN_SYSTEM_FIELD_IDS = new Set([
  'id',
  'ownerName',
  'organizationUnit',
  'currentStage',
  'nextDeadline',
]);

/** 跨表引用映射在方案 UI 中使用的模块标识。 */
export const LITIGATION_SHEET_REFERENCE_MODULE = 'legal-litigation-sheet-ref-v1';

/**
 * 判断标准字段在方案映射中的数据来源分类。
 * @param fieldId 标准字段 ID
 */
export function litigationPlanMappingSourceKind(
  fieldId: string,
): 'litigation' | 'choice' | 'manual' | 'computed' | 'custom' | 'reference' {
  if (fieldId === 'seqNo') return 'computed';
  if (fieldId === 'internalNo') return 'custom';
  if (fieldId === 'latestProgress') return 'reference';
  if (fieldId === 'disputeType' || fieldId === 'instance' || fieldId === 'position') return 'choice';
  if (LITIGATION_PLAN_SYSTEM_FIELD_IDS.has(fieldId)) return 'litigation';
  return 'manual';
}

/** 方案映射「案件类型」列的可选值（与内部案号取号类型一致）。 */
export const LITIGATION_CASE_TYPE_CHOICES: readonly LitigationNumberingKind[] = ['ss', 'zx', 'zc', 'ldzc'];

/**
 * 由表头或单元格文本解析案件类型。
 * @param raw 用户输入或 Excel 单元格原文
 */
export function parseLitigationCaseTypeChoice(raw: string): LitigationNumberingKind | null {
  const key = labelKey(raw);
  if (!key) return null;
  if (key === 'ss' || key === 'zx' || key === 'zc' || key === 'ldzc') return key;
  if (key.includes('劳动')) return 'ldzc';
  if (key.includes('执行')) return 'zx';
  if (key.includes('仲裁')) return 'zc';
  if (key.includes('诉讼')) return 'ss';
  return null;
}

/** 方案映射「审级」列的可选值（不含执行，执行由案件类型带入）。 */
export const LITIGATION_INSTANCE_CHOICES: readonly LitigationInstance[] = ['first', 'second', 'retrial'];

/**
 * 由表头或单元格文本解析审级（一审、二审、再审）。
 * @param raw 用户输入或 Excel 单元格原文
 */
export function parseLitigationInstanceChoice(raw: string): LitigationInstance | null {
  const key = labelKey(raw);
  if (!key) return null;
  if (key === 'first' || key === 'second' || key === 'retrial') return key;
  if (key.includes('再审')) return 'retrial';
  if (key.includes('二审') || (key.includes('二') && !key.includes('劳动'))) return 'second';
  if (key.includes('一审') || key.includes('初审') || key === 'firstinstance') return 'first';
  return null;
}

/** 方案映射「我方地位」列的可选值。 */
/** 案件性质的存储值与表头选项。 */
export const LITIGATION_CASE_NATURE_LABELS = {
  civil: '民事',
  administrative: '行政',
} as const;

/**
 * 把表头或单元格文本解析成案件性质。无法识别时返回空字符串。
 * @param raw 用户输入或 Excel 单元格原文
 */
export function parseLitigationCaseNature(raw: string): LitigationCaseNature | '' {
  const key = labelKey(raw);
  if (key === '民事' || key === 'civil') return 'civil';
  if (key === '行政' || key === 'administrative') return 'administrative';
  return '';
}

export const LITIGATION_POSITION_CHOICES: readonly LitigationPosition[] = ['plaintiff', 'defendant', 'third'];

/** 方案映射与导出列用的我方地位简称（与 Excel 下拉一致）。 */
export const LITIGATION_POSITION_CHOICE_LABELS: Record<LitigationPosition, string> = {
  plaintiff: '原告',
  defendant: '被告',
  third: '第三人',
};

/**
 * 由表头或单元格文本解析我方地位。
 * @param raw 用户输入或 Excel 单元格原文
 */
export function parseLitigationPositionChoice(raw: string): LitigationPosition | null {
  const key = labelKey(raw);
  if (!key) return null;
  if (key === 'plaintiff' || key === 'defendant' || key === 'third') return key;
  if (key.includes('第三')) return 'third';
  if (key.includes('被告') || key.includes('被申请')) return 'defendant';
  if (key.includes('原告') || key.includes('申请')) return 'plaintiff';
  return null;
}

export const LITIGATION_PLAN_STANDARD_FIELDS: readonly LitigationPlanStandardField[] = [
  {id: 'seqNo', label: '序号', aliases: ['序号', '编号', '行号'], group: 'meta', listDefault: false},
  {id: 'id', label: '系统编号', aliases: ['系统编号', '案件ID'], group: 'meta', listDefault: false},
  {id: 'ownerName', label: '填写人', aliases: ['填写人', '录入人', '负责人'], group: 'meta', listDefault: false},
  {id: 'internalNo', label: '内部案号', aliases: ['内部案号', '内部编号', '案件内部编号'], group: 'basic', listDefault: true},
  {id: 'courtNo', label: '案号', aliases: ['案号', '法院案号', '仲裁案号', '受理号', '法院/仲裁案号'], group: 'basic', listDefault: false},
  {id: 'relatedCourtNo', label: '诉讼与执行关联案号', aliases: ['诉讼与执行关联案号', '关联案号'], group: 'basic', listDefault: false},
  {id: 'caseName', label: '案件名称', aliases: ['案件名称', '纠纷名称'], group: 'basic', listDefault: false},
  {id: 'title', label: '案由', aliases: ['案由'], group: 'basic', listDefault: true},
  {id: 'caseNature', label: '案件性质', aliases: ['案件性质'], group: 'basic', listDefault: true},
  {id: 'disputeType', label: '类型', aliases: ['类型', '争议类型', '案件类型'], group: 'basic', listDefault: true},
  {id: 'instance', label: '当前审级', aliases: ['当前审级', '审级', '程序审级'], group: 'basic', listDefault: false},
  {id: 'position', label: '我方地位', aliases: ['我方地位', '诉讼地位', '地位'], group: 'party', listDefault: false},
  {id: 'ourParty', label: '我方', aliases: ['我方', '我方主体', '原告', '申请人', '原告/执行申请人', '原告/申请人'], group: 'party', listDefault: false},
  {id: 'plaintiffParty', label: '原告/上诉人/申请人', aliases: ['原告/上诉人/申请人', '原告/执行申请人', '原告/申请人', '执行申请人'], group: 'party', listDefault: false},
  {id: 'opponent', label: '被告/被上诉人/被申请人', aliases: ['被告/被上诉人/被申请人', '对方', '被告', '被申请人', '对方当事人', '被告/被执行人', '被执行人', '被告/被申请人'], group: 'party', listDefault: true},
  {id: 'thirdParty', label: '第三人', aliases: ['第三人', '有独立请求权第三人', '无独立请求权第三人'], group: 'party', listDefault: false},
  {id: 'venue', label: '管辖法院或仲裁机构的名称', aliases: ['管辖', '管辖法院', '仲裁机构', '管辖法院或仲裁机构的名称'], group: 'basic', listDefault: true},
  {id: 'filingDate', label: '立案日', aliases: ['立案日', '立案日期', '收案日期', '申请立案日', '申请立案日期'], group: 'basic', listDefault: false},
  {id: 'amount', label: '标的额', aliases: ['标的额', '争议金额', '涉案金额', '标的'], group: 'basic', listDefault: false},
  {id: 'currentStage', label: '当前阶段', aliases: ['当前阶段', '阶段'], group: 'procedure', listDefault: true},
  {id: 'closeMode', label: '结案方式', aliases: ['结案方式'], group: 'result', listDefault: false},
  {id: 'enforcementStatus', label: '执行状态', aliases: ['执行状态'], group: 'result', listDefault: false},
  {id: 'latestProgress', label: '最新进展', aliases: ['最新进展', '案情进展', '进展', '案件进展记录', '案件进展'], group: 'procedure', listDefault: true},
  {id: 'nextDeadline', label: '下一期限', aliases: ['下一期限', '期限', '到期日'], group: 'procedure', listDefault: false},
  {id: 'handler', label: '承办人', aliases: ['承办人', '经办人', '跟进人'], group: 'meta', listDefault: false},
  {id: 'counsel', label: '代理律师', aliases: ['代理律师', '律师', '外聘律师'], group: 'meta', listDefault: false},
  {id: 'counselOrg', label: '代理机构', aliases: ['代理机构', '律所', '律师事务所', '代理律所', '外聘律所', '委托律所', '代理单位'], group: 'meta', listDefault: false},
  {id: 'organizationUnit', label: '所属单位', aliases: ['所属单位'], group: 'meta', listDefault: true},
  {id: 'claimSummary', label: '诉讼请求/申请执行标的', aliases: ['诉讼请求/申请执行标的', '诉讼请求', '仲裁请求', '请求事项', '申请执行标的'], group: 'basic', listDefault: false},
  {id: 'resultSummary', label: '判决/裁决结果', aliases: ['判决/裁决结果', '判决支持标的金额/执行到位金额', '判决支持的金额/执行到位金额', '判决支持标的金额', '执行到位金额', '结果摘要', '裁判结果', '裁决结果'], group: 'result', listDefault: false},
  {id: 'awardedAmount', label: '判决（裁决）支持的金额/执行到位的金额', aliases: ['判决（裁决）支持的金额/执行到位的金额', '判决（裁决）支持的金额', '执行到位的金额', '非货币判决结果', '非货币执行结果', '非货币判决结果/非货币执行结果', '非货币结果', '非金钱判决结果'], group: 'result', listDefault: false},
  {id: 'involvedAmount', label: '案涉金额', aliases: ['案涉金额', '是否结案', '是否已结案', '结案状态'], group: 'result', listDefault: true},
  {id: 'closedAt', label: '结案日', aliases: ['结案日', '结案日期', '归档日', '诉讼结案日'], group: 'result', listDefault: false},
];

/** 各标准字段在表格中的填写建议。诉讼台账不从报关单/发票抽字段，需按规则手填或由系统回写。 */
export const LITIGATION_FIELD_FILL_GUIDES: Record<string, LitigationFieldFillGuide> = {
  seqNo: {rule: '案件总表行号。同一工作表从 1 起连续编号，不要跳号。页面列表按当前可见行自动生成，Excel 可留空或按区段自编。', example: '1'},
  id: {rule: '系统内部编号，创建案件后自动写入。请勿手填或改动，否则附件和进展对不上。', example: '（系统生成）'},
  ownerName: {rule: '按登录人自动写入的填写人。协作台账中用于划分连续区段，请勿手改。', example: '张三'},
  organizationUnit: {rule: '按填写人自动写入所属单位。方案创建者在台账方案里选定显示全称、简称或代码，团队成员不能改这个选项，也不能在台账里手填。内部案号仍只用单位代码。', example: '某某科技有限公司'},
  internalNo: {rule: '本台账主键，由系统按方案规则自动生成，办案人员不用手填。申请立案日和案件类型都选定之后才取号；同年份、同单位、同类型的流水号依次递增。要件未齐时不占号。同一台账内不可重复；四张表必须完全一致。不要用法院/仲裁案号代替。取号后不要手改。', example: '2026-ss-001'},
  courtNo: {rule: '案号层次随案件类型变化。诉讼只填一审案号、二审案号、再审案号。执行只填执行案号。仲裁只填仲裁案号。劳动仲裁只填劳动仲裁案号。没有的层次不要加行，不要用内部案号代替。', example: '（2026）京01民初1号'},
  relatedCourtNo: {rule: '只填一个关联案号。诉讼、仲裁或劳动仲裁文书生效并进入执行时，填执行案号；这一行本身是执行案件时，填生效裁判文书案号。', example: '（2026）京01执1号'},
  caseName: {rule: '便于内部识别的案件简称或标题，可自拟；与标准「案由」不同，不需要从案由库点选。', example: '甲公司诉乙公司买卖合同案'},
  title: {rule: '须从最高人民法院民事第三级案由库中选择，输入至少 2 个字检索后点选；不可自造案由。', example: '买卖合同纠纷'},
  caseNature: {
    rule: '从固定列表中选择案件性质，排在案件类型之前。只包括民事和行政。民事继续使用诉讼、仲裁、执行、劳动仲裁。内部案号仍只按案件类型取号，不把案件性质编进案号。',
    allowed: '民事 / 行政',
    example: '民事',
  },
  disputeType: {
    rule: '从固定列表中选择，不要自造类型名；与新建案件时的「案件类型」及内部案号取号一致。',
    allowed: '诉讼 / 执行 / 仲裁 / 劳动仲裁',
    example: '诉讼',
  },
  instance: {
    rule: '由已经打开的案号行自动带出，办案人员不用手选，也不必先写上案号。诉讼案件默认显示一审。打开再审案号那一格则为再审，否则打开二审案号那一格则为二审。案件类型选「执行」时，审级为执行。',
    allowed: '一审 / 二审 / 再审 / 执行',
    example: '二审',
  },
  position: {
    rule: '从固定列表中选择我方在本案中的主地位，一案只填一项；仲裁场景下「原告」含申请人，「被告」含被申请人。',
    allowed: '原告 / 被告 / 第三人',
    example: '原告',
  },
  ourParty: {rule: '我方主体全称，与营业执照或授权文件一致。多名我方在案件详情的当事人里补充。', example: '某某公司'},
  plaintiffParty: {rule: '本案原告或执行申请人的全称，与我方不是同一格。我方是原告时，仍需在本列另行填写。', example: '某某公司'},
  opponent: {rule: '被告或被执行人的全称。多名时总表写主要一名，其余在案件详情的当事人里补充。', example: '某某商贸有限公司'},
  thirdParty: {rule: '第三人全称。与被告、被执行人分开填写。', example: '某某科技有限公司'},
  venue: {rule: '管辖随案件类型变化。诉讼填写一审法院、二审法院，执行只填执行法院。法院名称输入时立即从中国法院资料库模糊检索，必须点选名录中的全称，不能自造。仲裁只填仲裁机构，劳动仲裁只填劳动仲裁机构。', example: '北京市第一中级人民法院'},
  filingDate: {rule: '申请立案日。点开格子后选择年、月、日，再点确认。内部案号中的年份取这一天的年份。统计「本年新收」以此日为准；未立案留空。', example: '2022-01-26'},
  amount: {rule: '争议标的额，只填数字，单位为元。外币可在数字后加 USD 等币种。', example: '100000'},
  currentStage: {
    rule: '选项随案件类型变化。诉讼案件：立案受理 / 举证 / 开庭 / 裁判/调解 / 上诉 / 二审举证 / 二审开庭 / 二审裁判 / 再审立案 / 再审举证 / 再审开庭 / 再审裁判 / 执行 / 已结案。仲裁案件：申请/答辩 / 组庭 / 开庭 / 裁决 / 执行 / 已结案。执行案件：立案受理 / 财产查控 / 财产处置 / 案款发放 / 执行和解 / 已结案。选「已结案」即结案。',
    allowed: '立案受理 / 举证 / 开庭 / 裁判/调解 / 上诉 / 二审举证 / 二审开庭 / 二审裁判 / 再审立案 / 再审举证 / 再审开庭 / 再审裁判 / 执行 / 已结案 / 财产查控 / 财产处置 / 案款发放 / 执行和解 / 申请/答辩 / 组庭 / 裁决',
    example: '立案受理',
  },
  latestProgress: {
    rule: '不在总表手填。请映射为引用「进展记录」表中该案的「内容」列，导出与列表自动取最新一条进展。',
    example: '已立案，等待开庭',
  },
  nextDeadline: {rule: '可留空：系统取未完成关键期限中最早的计划日。手填须为年-月-日。', example: '2026-03-01'},
  closeMode: {rule: '只有当前阶段选择「已结案」后才填写。不是已结案时留空，不要选择。诉讼、仲裁案件从「判决 / 调解 / 撤诉 / 裁定」中选择。执行案件从「执行完毕 / 终结本次执行程序 / 终结执行 / 销案 / 驳回申请 / 不予执行」中选择。', allowed: '判决 / 调解 / 撤诉 / 裁定 / 执行完毕 / 终结本次执行程序 / 终结执行 / 销案 / 驳回申请 / 不予执行', example: '判决'},
  closedAt: {rule: '只有当前阶段选择「已结案」后才填写结案日。不是已结案时留空，不要打开日期。点开格子后选择年、月、日，再点确认。统计「本年结案」以此日为准。', example: '2026-06-20'},
  enforcementStatus: {rule: '执行状态与诉讼结果分开填。未申请执行填「未进入执行」。', allowed: '未进入执行 / 执行中 / 终本执行 / 执行完毕', example: '未进入执行'},
  handler: {rule: '企业内部承办人姓名，一人一案主责。', example: '张三'},
  counsel: {rule: '外聘代理律师姓名。没有外所可留空。', example: '李律师'},
  counselOrg: {rule: '外聘代理机构（律所）全称，与委托合同一致。没有外所可留空。', example: '某某律师事务所'},
  claimSummary: {rule: '先填写原告请求。同一方有多条请求时，在格子里回车换行继续写。需要反请求时，点这一行后面的加号再填写，反请求也可以换行写多条。案件类型为执行时只填申请执行标的，没有反请求。', example: '1、请求支付货款及逾期利息'},
  resultSummary: {rule: '只有当前阶段选择「已结案」后才填写。不是已结案时留空，不要填写。先填写本诉结果。同一方有多条判决或裁决结果时，在格子里回车换行继续写。需要反诉或反请求的结果时，点这一行后面的加号再填写。执行案件只填执行结果，没有反诉结果。', example: '1、支持货款本金'},
  awardedAmount: {rule: '只有当前阶段选择「已结案」后才填写。不是已结案时留空，不要输入金额。诉讼案件先填本诉获支持金额，需要反诉获支持金额时点这一行后面的加号。仲裁和劳动仲裁先填本请求获支持金额，需要反请求获支持金额时点加号。只填数字，单位为元。执行案件只填执行到位的金额。', example: '100000'},
  involvedAmount: {
    rule: '由用户自行填写金额，单位为元。诉讼案件先填本诉金额，需要反诉时点这一行后面的加号，再填反诉金额。仲裁和劳动仲裁先填本请求金额，需要反请求时点加号再填反请求金额。执行案件只填申请执行标的金额。是否结案不再单列，选当前阶段「已结案」即结案。',
    example: '100000',
  },
  caseBackground: {rule: '该案的内容介绍，一案一行。与案件名称格右下角打开的案件背景窗口是同一段文字，导出写在「案件背景」表。', example: '因车位使用权发生争议。'},
  partyRole: {rule: '该行当事人角色。同一案件可有多行。当事人在案件详情中维护，不写入「案件背景」表。', allowed: '我方 / 对方 / 第三人', example: '对方'},
  partyName: {rule: '该行当事人全称，与证件或诉状一致。', example: '某某商贸有限公司'},
  partyPrimary: {rule: '同一角色只标一名主当事人为「是」，其余为「否」。总表「我方/对方」取主当事人。', allowed: '是 / 否', example: '是'},
  partyContact: {rule: '联系人或电话，可留空。', example: '王某 13800000000'},
  nodeName: {rule: '程序节点名称。建议与模板节点一致，便于统计当前阶段。', example: '开庭'},
  plannedDate: {rule: '计划完成日，格式年-月-日。关键期限据此进入期限队列。', example: '2026-03-01'},
  completedDate: {rule: '实际完成日。未完成必须留空，不要填预计日期。', example: '2026-03-01'},
  keyDeadline: {rule: '是否纳入期限监控。填「是」才会出现在期限队列和逾期统计。', allowed: '是 / 否', example: '是'},
  deadlineKind: {rule: '期限种类，便于筛选。不确定时填「自定义」。', allowed: '自定义 / 举证 / 开庭 / 上诉 / 执行', example: '开庭'},
  nodeNote: {rule: '该节点结果或说明，可留空。', example: '第一次开庭已完成'},
  progressDate: {rule: '该条进展发生日，格式年-月-日。一条进展只记一件事实。', example: '2026-01-08'},
  progressContent: {rule: '进展正文。按时间追加，不要覆盖旧记录；不要把全部历史写进总表。', example: '已立案，等待开庭通知'},
  progressAuthor: {rule: '记录人。可留空，系统默认用案件填写人。', example: '张三'},
};

/**
 * 字段填写规则种类（后台管理员可选择）。
 * 决定该字段的数据来源与填写方式，客户端表单会据此给出提示。
 */
export type LitigationFieldRuleKind =
  | 'system'      // 系统自动写入（不可手改）
  | 'manual'      // 人工填写
  | 'choice'      // 固定选项中选择
  | 'date'        // 年月日选择后确认
  | 'cause-library' // 案由库选择（最高法第三级案由）
  | 'court-library' // 法院名录选择（中国法院资料库）
  | 'reference'   // 跨表引用（取其他工作表的列）
  | 'computed';   // 系统计算得出

export type LitigationFieldRuleKindInfo = {
  id: LitigationFieldRuleKind;
  label: string;
  /** 后台下拉里的一句话说明 */
  hint: string;
  /** 客户端表单是否只读 */
  readOnly: boolean;
};

export const LITIGATION_FIELD_RULE_KINDS: readonly LitigationFieldRuleKindInfo[] = [
  {id: 'manual', label: '人工填写', hint: '由办案人员按规则手工录入', readOnly: false},
  {id: 'system', label: '系统自动写入', hint: '系统生成或回写，办案人员不可修改', readOnly: true},
  {id: 'choice', label: '固定选项', hint: '只能从预设选项中选择，不能自造', readOnly: false},
  {id: 'date', label: '年月日选择', hint: '弹出年月日，选好后点确认写入，不用手打日期', readOnly: false},
  {id: 'cause-library', label: '案由库选择', hint: '必须从最高法第三级案由库检索后点选', readOnly: false},
  {id: 'court-library', label: '法院名录', hint: '法院层次必须从中国法院资料库检索后点选；仲裁机构仍可手填', readOnly: false},
  {id: 'reference', label: '跨表引用', hint: '取其他工作表的对应列，不在本表手填', readOnly: true},
  {id: 'computed', label: '系统计算', hint: '由其他字段计算得出，不单独填写', readOnly: true},
];

const RULE_KIND_BY_ID = new Map(LITIGATION_FIELD_RULE_KINDS.map((item) => [item.id, item]));

/** 规则种类信息；未知返回 undefined。 */
export function litigationFieldRuleKindInfo(kind: string): LitigationFieldRuleKindInfo | undefined {
  return RULE_KIND_BY_ID.get(kind as LitigationFieldRuleKind);
}

/**
 * 代码内置的默认规则种类。管理员可在后台逐字段覆盖。
 */
export const LITIGATION_FIELD_RULE_KIND_DEFAULTS: Record<string, LitigationFieldRuleKind> = {
  seqNo: 'computed',
  id: 'system',
  ownerName: 'system',
  organizationUnit: 'system',
  internalNo: 'system',
  latestProgress: 'reference',
  nextDeadline: 'computed',
  caseNature: 'choice',
  disputeType: 'choice',
  instance: 'computed',
  position: 'choice',
  closeMode: 'choice',
  enforcementStatus: 'choice',
  currentStage: 'choice',
  keyDeadline: 'choice',
  deadlineKind: 'choice',
  partyPrimary: 'choice',
  title: 'cause-library',
  venue: 'court-library',
  partyRole: 'choice',
  filingDate: 'date',
  closedAt: 'date',
  plannedDate: 'date',
  completedDate: 'date',
  progressDate: 'date',
};

/** 未在上面列出的字段一律按人工填写。 */
export function defaultFieldRuleKind(fieldId: string): LitigationFieldRuleKind {
  return LITIGATION_FIELD_RULE_KIND_DEFAULTS[fieldId] ?? 'manual';
}

/**
 * 管理员在后台对某个字段填写规则的覆盖配置。
 * 只覆盖填写的字段，其余沿用代码内置默认值。
 */
export type LitigationFieldRuleOverride = {
  ruleKind?: LitigationFieldRuleKind;
  rule?: string;
  allowed?: string;
  example?: string;
  /** 案号字段各层次的示例。只对法院/仲裁案号生效。 */
  courtNumberExamples?: Partial<Record<LitigationCourtNumberLayer, string>>;
  /** 格子里左侧固定名称。支持金额用来改「本诉获支持金额」「反诉获支持金额」。 */
  rowLabels?: {primary?: string; counter?: string};
};

/** 平台配置里的字段规则覆盖表：字段 ID → 覆盖项。 */
export type LitigationFieldRuleOverrides = Record<string, LitigationFieldRuleOverride>;

/**
 * 计算某字段最终生效的填写规则：后台覆盖优先，其次代码默认。
 * @param fieldId 标准字段 ID
 * @param overrides 后台覆盖表
 */
export function resolveLitigationFieldRule(
  fieldId: string,
  overrides?: LitigationFieldRuleOverrides | null,
): LitigationFieldFillGuide & {ruleKind: LitigationFieldRuleKind; customized: boolean} {
  const base = LITIGATION_FIELD_FILL_GUIDES[fieldId];
  const override = overrides?.[fieldId];
  const ruleKind = override?.ruleKind ?? defaultFieldRuleKind(fieldId);
  return {
    ruleKind,
    customized: Boolean(override && (override.rule || override.allowed || override.example || override.ruleKind || override.courtNumberExamples || override.rowLabels)),
    rule: String(override?.rule ?? base?.rule ?? '').trim(),
    allowed: String(override?.allowed ?? base?.allowed ?? '').trim() || undefined,
    example: String(override?.example ?? base?.example ?? '').trim() || undefined,
  };
}

/**
 * 读取字段填写规则。
 * @param fieldId 标准字段 ID
 * @param internalNoRule 方案中的内部案号规则
 */
export function litigationFieldFillGuide(fieldId: string, internalNoRule?: LitigationInternalNoRule): LitigationFieldFillGuide | undefined {
  if (fieldId === 'internalNo') return litigationInternalNoFillGuide(internalNoRule);
  return LITIGATION_FIELD_FILL_GUIDES[fieldId];
}

/**
 * 默认内部案号规则：年份 + 案件类型 + 流水号。
 */
export function defaultLitigationInternalNoRule(): LitigationInternalNoRule {
  return {
    parts: defaultInternalNoParts(),
    separator: '-',
    serialWidth: 3,
    orgCode: '',
    otherText: '',
    caseTypeCodes: {...DEFAULT_CASE_TYPE_CODES},
    note: '',
  };
}

/**
 * 规范化方案中的内部案号规则。兼容旧的前缀/年份/流水配置。
 * @param value 原始配置
 */
export function normalizeLitigationInternalNoRule(value: unknown): LitigationInternalNoRule {
  const record = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const separator = String(record.separator ?? '-').slice(0, 3);
  const serialWidth = Math.max(1, Math.min(6, Number(record.serialWidth ?? record.seqWidth) || 3));
  const orgCode = sanitizeOrgCode(String(record.orgCode ?? record.prefix ?? ''), separator);
  const otherText = sanitizeOtherText(String(record.otherText ?? ''), separator);
  const note = String(record.note ?? '').trim().slice(0, 200);
  const caseTypeCodes = normalizeCaseTypeCodes(record.caseTypeCodes);
  const rawParts = Array.isArray(record.parts) ? record.parts : null;
  const hasLegacy = 'prefix' in record || 'mode' in record || 'includeYear' in record || 'seqWidth' in record || 'orgCode' in record;
  const parts = rawParts?.length
    ? normalizeInternalNoParts(rawParts)
    : hasLegacy
      ? migrateLegacyInternalNoParts(record, orgCode)
      : defaultInternalNoParts();
  return {parts, separator, serialWidth, orgCode, otherText, caseTypeCodes, note};
}

/**
 * 从争议类型/审级或显式代号解析编号用案件类型。
 * @param input 立案信息
 */
export function deriveLitigationNumberingKind(input: LitigationInternalNoContext = {}): LitigationNumberingKind {
  const explicit = String(input.numberingKind || '').trim().toLocaleLowerCase();
  if (explicit === 'ss' || explicit === 'zx' || explicit === 'zc' || explicit === 'ldzc') return explicit;
  const dispute = String(input.disputeType || '').trim().toLocaleLowerCase();
  if (dispute === 'labor' || dispute === 'ldzc') return 'ldzc';
  if (dispute === 'arbitration' || dispute === 'zc') return 'zc';
  const instance = String(input.instance || '').trim().toLocaleLowerCase();
  const enforcement = String(input.enforcementStatus || '').trim().toLocaleLowerCase();
  if (explicit === 'enforcement' || instance === 'enforcement' || (enforcement && enforcement !== 'none')) return 'zx';
  return 'ss';
}

/**
 * 编号类型对应的争议类型与审级。
 * @param kind 编号用案件类型
 */
export function caseFieldsFromNumberingKind(kind: LitigationNumberingKind): {
  disputeType: LitigationDisputeType;
  instance: LitigationInstance;
  enforcementStatus: LitigationEnforcementStatus;
} {
  if (kind === 'zx') return {disputeType: 'litigation', instance: 'enforcement', enforcementStatus: 'in_progress'};
  if (kind === 'zc') return {disputeType: 'arbitration', instance: 'first', enforcementStatus: 'none'};
  if (kind === 'ldzc') return {disputeType: 'labor', instance: 'first', enforcementStatus: 'none'};
  return {disputeType: 'litigation', instance: 'first', enforcementStatus: 'none'};
}

/**
 * 内部案号里的所属单位代号。
 * 方案管理员用自己在台账方案里填写的单位代码；协作人员用所属协作单位的代码。
 * @param plan 台账方案
 * @param userId 案件填写人
 * @param workbookCreatedBy 台账创建人；方案上没有创建人时用来识别管理员
 */
export function litigationPlanOrgCodeForUser(
  plan: Pick<LitigationLedgerPlan, 'createdBy' | 'creatorOrganizationCode' | 'collaborationUnits' | 'teamEditors'>,
  userId: string,
  workbookCreatedBy = '',
): string {
  const editor = (plan.teamEditors || []).find((item) => item.userId === userId);
  if (editor?.unitId === LITIGATION_CREATOR_UNIT_ID) return String(plan.creatorOrganizationCode || '').trim();
  if (editor) {
    return String((plan.collaborationUnits || []).find((item) => item.id === editor.unitId)?.code || '').trim();
  }
  if (userId && (userId === String(plan.createdBy || '').trim() || userId === workbookCreatedBy)) {
    return String(plan.creatorOrganizationCode || '').trim();
  }
  return '';
}

/** 决定台账「所属单位」写哪一段文字时要用到的方案字段。 */
export type LitigationOrganizationSettings = Pick<
  LitigationLedgerPlan,
  'createdBy' | 'organizationLabelKind' | 'creatorOrganizationName' | 'creatorOrganizationShortName' | 'creatorOrganizationCode' | 'collaborationUnits' | 'teamEditors'
>;

/**
 * 按显示选项取出单位的全称、简称或代码。缺字段时返回空串，不抛错。
 * @param kind 显示选项
 * @param name 全称
 * @param shortName 简称
 * @param code 代码
 */
function organizationLabelText(kind: LitigationOrganizationLabelKind, name?: string, shortName?: string, code?: string): string {
  if (kind === 'shortName') return String(shortName || '').trim();
  if (kind === 'code') return String(code || '').trim();
  return String(name || '').trim();
}

/**
 * 优先用现行方案上的单位资料；某一项是空的时，保留台账副本里已经登记过的文字。
 * 这样把显示从全称改成简称后，简称不会因为副本没跟上而变成空。
 * @param live 现行方案
 * @param snapshot 台账里固化的方案副本
 */
export function litigationOrganizationSettingsForLabels(
  live: LitigationOrganizationSettings,
  snapshot: LitigationOrganizationSettings = {},
): LitigationOrganizationSettings {
  const filled = (value: string | undefined, fallback: string | undefined) => String(value || '').trim() || String(fallback || '').trim();
  const snapshotUnits = new Map((snapshot.collaborationUnits || []).map((unit) => [unit.id, unit]));
  const sourceUnits = live.collaborationUnits !== undefined ? live.collaborationUnits : (snapshot.collaborationUnits || []);
  return {
    createdBy: filled(live.createdBy, snapshot.createdBy),
    organizationLabelKind: parseLitigationOrganizationLabelKind(String(live.organizationLabelKind || snapshot.organizationLabelKind || 'name')),
    creatorOrganizationName: filled(live.creatorOrganizationName, snapshot.creatorOrganizationName),
    creatorOrganizationShortName: filled(live.creatorOrganizationShortName, snapshot.creatorOrganizationShortName),
    creatorOrganizationCode: filled(live.creatorOrganizationCode, snapshot.creatorOrganizationCode),
    collaborationUnits: sourceUnits.map((unit) => {
      const previous = snapshotUnits.get(unit.id);
      return {
        id: unit.id,
        name: filled(unit.name, previous?.name),
        shortName: filled(unit.shortName, previous?.shortName),
        code: filled(unit.code, previous?.code),
      };
    }),
    teamEditors: live.teamEditors !== undefined ? live.teamEditors : snapshot.teamEditors,
  };
}

/**
 * 台账「所属单位」列要写入的文字。
 * 方案创建者用自己登记的单位，协作人员用所挂协作单位；具体写全称、简称还是代码，由方案上的选项决定。
 * @param plan 台账方案
 * @param userId 案件填写人
 * @param workbookCreatedBy 台账创建人；方案上没有创建人时用来识别管理员
 */
export function litigationPlanOrganizationLabelForUser(
  plan: LitigationOrganizationSettings,
  userId: string,
  workbookCreatedBy = '',
): string {
  const kind = parseLitigationOrganizationLabelKind(String(plan.organizationLabelKind || 'name'));
  const creatorId = String(plan.createdBy || '').trim();
  if (userId && (userId === creatorId || userId === workbookCreatedBy)) {
    return organizationLabelText(kind, plan.creatorOrganizationName, plan.creatorOrganizationShortName, plan.creatorOrganizationCode);
  }
  const editor = (plan.teamEditors || []).find((item) => item.userId === userId);
  if (!editor) return '';
  if (editor.unitId === LITIGATION_CREATOR_UNIT_ID) return organizationLabelText(kind, plan.creatorOrganizationName, plan.creatorOrganizationShortName, plan.creatorOrganizationCode);
  const unit = (plan.collaborationUnits || []).find((item) => item.id === editor.unitId);
  if (!unit) return '';
  return organizationLabelText(kind, unit.name, unit.shortName, unit.code);
}

/**
 * 按方案规则拼一条内部案号。
 * @param rule 编号规则
 * @param context 立案日期、案件类型与单位代号
 * @param serial 流水号
 */
export function formatLitigationInternalNo(
  rule: LitigationInternalNoRule,
  context: LitigationInternalNoContext,
  serial: number,
): string {
  const tokens = outputKinds(rule, context).map((kind) => tokenForKind(kind, rule, context, serial));
  return tokens.filter(Boolean).join(rule.separator).slice(0, LITIGATION_INTERNAL_NO_MAX_LENGTH);
}

/**
 * 方案预览用的下一条样式。
 * @param rule 编号规则
 * @param context 预览用立案信息
 */
export function previewLitigationInternalNo(rule: LitigationInternalNoRule, context?: LitigationInternalNoContext): string {
  const year = new Date().getFullYear();
  return formatLitigationInternalNo(normalizeLitigationInternalNoRule(rule), {
    filingDate: context?.filingDate || `${year}-01-08`,
    numberingKind: context?.numberingKind || 'ss',
    disputeType: context?.disputeType,
    instance: context?.instance,
    enforcementStatus: context?.enforcementStatus,
    orgCode: context?.orgCode,
  }, 1);
}

/**
 * 按本台账已有案号取下一个自动编号。同类、同年月日等已启用前缀各自从 1 起号。
 * @param rule 编号规则
 * @param existing 已占用案号
 * @param context 当前案件
 */
export function nextLitigationInternalNo(
  rule: LitigationInternalNoRule,
  existing: string[],
  context: LitigationInternalNoContext = {},
): string {
  const normalized = normalizeLitigationInternalNoRule(rule);
  const key = identityKey(normalized, context);
  let max = 0;
  for (const value of existing) {
    const parsed = parseInternalNoSerial(normalized, value, context);
    if (parsed && parsed.key === key) max = Math.max(max, parsed.serial);
  }
  return formatLitigationInternalNo(normalized, context, max + 1);
}

/**
 * 已生成的内部案号如果缺了后来启用的单位代号，按同一流水补上。
 * 已经符合当前规则的案号保持不变。
 * @param rule 当前编号规则
 * @param existing 已有案号
 * @param context 立案信息与单位代号
 */
export function alignLitigationInternalNo(
  rule: LitigationInternalNoRule,
  existing: string,
  context: LitigationInternalNoContext,
): string {
  const text = String(existing || '').trim();
  if (!text) return text;
  const normalized = normalizeLitigationInternalNoRule(rule);
  if (parseInternalNoSerial(normalized, text, context)) return text;
  const parsed = parseInternalNoSerial(normalized, text, {...context, orgCode: ''});
  if (!parsed) return text;
  return formatLitigationInternalNo(normalized, context, parsed.serial) || text;
}

/**
 * 自动内部案号里的案件类型代号与当前案件类型不一致时，换成对应代号。
 * 年份、单位代号和流水号保持不变；新编号已被占用时顺延流水号。手工编号不改。
 * @param rule 当前编号规则
 * @param existing 已有案号
 * @param context 案件类型与单位代号
 * @param taken 台账里其他案件已占用的案号
 */
export function syncLitigationInternalNoCaseType(
  rule: LitigationInternalNoRule,
  existing: string,
  context: LitigationInternalNoContext,
  taken: string[] = [],
): string {
  const text = String(existing || '').trim();
  if (!text) return text;
  const normalized = normalizeLitigationInternalNoRule(rule);
  const tokens = autoInternalNoTokens(normalized, text, context);
  if (!tokens) return text;
  const kinds = outputKinds(normalized, context);
  const typeIndex = kinds.indexOf('caseType');
  const expected = normalized.caseTypeCodes[deriveLitigationNumberingKind(context)];
  if (typeIndex < 0 || !expected || tokens[typeIndex].toLocaleLowerCase() === expected.toLocaleLowerCase()) return text;
  const nextTokens = [...tokens];
  nextTokens[typeIndex] = expected;
  const rebuilt = nextTokens.join(normalized.separator);
  const occupied = new Set(taken.map((item) => item.trim().toLocaleLowerCase()).filter((item) => item && item !== text.toLocaleLowerCase()));
  if (!occupied.has(rebuilt.toLocaleLowerCase())) return rebuilt;
  return nextLitigationInternalNo(normalized, taken.filter((item) => item.trim().toLocaleLowerCase() !== text.toLocaleLowerCase()), context);
}

/**
 * 自动内部案号里的年、月、日改成申请立案日。
 * 单位代号、类型代号和流水号保持不变；新编号已被占用时顺延流水号。
 * 没有申请立案日，或案号不是按规则生成的，保持原样。
 * @param rule 当前编号规则
 * @param existing 已有案号
 * @param context 申请立案日与单位代号
 * @param taken 台账里其他案件已占用的案号
 */
export function syncLitigationInternalNoFilingYear(
  rule: LitigationInternalNoRule,
  existing: string,
  context: LitigationInternalNoContext,
  taken: string[] = [],
): string {
  const text = String(existing || '').trim();
  const filed = String(context.filingDate || '').trim();
  if (!text || !/^\d{4}-\d{2}-\d{2}$/.test(filed)) return text;
  const normalized = normalizeLitigationInternalNoRule(rule);
  const tokens = autoInternalNoTokens(normalized, text, context);
  if (!tokens) return text;
  const kinds = outputKinds(normalized, context);
  const date = filingDateParts(filed);
  const expected: Partial<Record<LitigationInternalNoPartKind, string>> = {
    year: date.year,
    month: date.month,
    day: date.day,
  };
  const nextTokens = [...tokens];
  let changed = false;
  for (const kind of ['year', 'month', 'day'] as const) {
    const index = kinds.indexOf(kind);
    const value = expected[kind];
    if (index < 0 || !value || nextTokens[index] === value) continue;
    nextTokens[index] = value;
    changed = true;
  }
  if (!changed) return text;
  const rebuilt = nextTokens.join(normalized.separator);
  const occupied = new Set(taken.map((item) => item.trim().toLocaleLowerCase()).filter((item) => item && item !== text.toLocaleLowerCase()));
  if (!occupied.has(rebuilt.toLocaleLowerCase())) return rebuilt;
  return nextLitigationInternalNo(normalized, taken.filter((item) => item.trim().toLocaleLowerCase() !== text.toLocaleLowerCase()), context);
}

/**
 * 内部案号的取号要件是否已经齐备。
 * 年份来自申请立案日，类型代号来自用户已经选定的案件类型。缺任一项都不占号。
 * @param item 立案日与尚未填写的字段
 */
export function litigationInternalNoBasisReady(item: {filingDate?: string; unfilledFields?: string[]}): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(item.filingDate || '').trim())) return false;
  return !(item.unfilledFields || []).includes('disputeType');
}

/**
 * 按要件决定内部案号。
 * 要件未齐时收回自动编号；齐了才按同年、同单位、同类型取下一个流水号。
 * 不符合当前规则的手工编号保持原样。
 * @param rule 编号规则
 * @param existing 已有案号
 * @param context 立案日、案件类型、单位代号，以及尚未填写的字段
 * @param taken 台账里其他案件已占用的案号
 */
export function prepareLitigationInternalNo(
  rule: LitigationInternalNoRule,
  existing: string,
  context: LitigationInternalNoContext & {unfilledFields?: string[]},
  taken: string[] = [],
): string {
  const text = String(existing || '').trim();
  const normalized = normalizeLitigationInternalNoRule(rule);
  if (!litigationInternalNoBasisReady(context)) {
    if (!text) return '';
    return autoInternalNoTokens(normalized, text, context) ? '' : text;
  }
  const candidate = !text
    ? nextLitigationInternalNo(normalized, taken, context)
    : syncLitigationInternalNoFilingYear(
      normalized,
      syncLitigationInternalNoCaseType(normalized, alignLitigationInternalNo(normalized, text, context), context, taken),
      context,
      taken,
    );
  return claimUniqueLitigationInternalNo(normalized, candidate, context, taken);
}

/**
 * 案号已被其他案件占用时，顺延流水号。调用方传入的占用列表不含本案自己的旧号。
 * @param rule 编号规则
 * @param candidate 准备采用的案号
 * @param context 立案信息
 * @param taken 其他案件已占用的案号
 */
function claimUniqueLitigationInternalNo(
  rule: LitigationInternalNoRule,
  candidate: string,
  context: LitigationInternalNoContext,
  taken: string[],
): string {
  const text = candidate.trim();
  if (!text) return text;
  const occupied = taken.some((item) => item.trim().toLocaleLowerCase() === text.toLocaleLowerCase());
  if (!occupied) return text;
  return nextLitigationInternalNo(rule, taken, context);
}

/** 写入台账单元格时，反诉请求与原告请求之间的分隔。 */
const LITIGATION_COUNTERCLAIM_MARKER = '反诉请求：';

/**
 * 诉讼请求列在 Excel 里的单元格文本。
 * 执行案件只写申请执行标的；其余案件在有反诉时另起一行。
 * @param item 案件
 */
export function formatLitigationClaimCell(item: Pick<LitigationCase, 'claimSummary' | 'disputeType' | 'instance' | 'enforcementStatus'> & {counterclaimSummary?: string}): string {
  const claim = String(item.claimSummary || '').trim();
  if (deriveLitigationNumberingKind(item) === 'zx') return claim;
  const counter = String(item.counterclaimSummary || '').trim();
  if (!counter) return claim;
  return claim ? `${claim}\n${LITIGATION_COUNTERCLAIM_MARKER}${counter}` : `${LITIGATION_COUNTERCLAIM_MARKER}${counter}`;
}

/**
 * 从单元格里找出反诉或反请求那一段。
 * 标记可以单独起行，也可以跟在分号后面。正文里的分号不会被当成新的一行。
 * @param raw 单元格文本
 * @param markers 反诉或反请求标记
 */
function splitLitigationCounterSection(raw: string, markers: readonly string[]): {primary: string; counter: string} | null {
  const text = String(raw || '').replace(/\r\n/g, '\n').trim();
  let bestIndex = -1;
  let bestMarker = '';
  for (const marker of markers) {
    const index = text.lastIndexOf(marker);
    if (index < 0) continue;
    if (index > bestIndex || (index === bestIndex && marker.length > bestMarker.length)) {
      bestIndex = index;
      bestMarker = marker;
    }
  }
  if (bestIndex < 0) return null;
  return {
    primary: text.slice(0, bestIndex).replace(/[；;\s]+$/g, '').trim(),
    counter: text.slice(bestIndex + bestMarker.length).trim(),
  };
}

/**
 * 两个纯金额用分号连在一行时，前一个是本诉，后一个是反诉。
 * @param raw 单元格文本
 */
function splitLitigationMoneyPair(raw: string): {primary: number; counter: number} | null {
  const parts = String(raw || '').split(/[；;]/).map((part) => part.trim()).filter(Boolean);
  if (parts.length !== 2) return null;
  const primary = parseLitigationMoney(parts[0]);
  const counter = parseLitigationMoney(parts[1]);
  if (primary === null || counter === null) return null;
  return {primary, counter};
}

/**
 * 把诉讼请求单元格拆回原告请求和反请求。
 * @param raw 单元格文本
 */
export function splitLitigationClaimCell(raw: string): {claim: string; counterclaim: string} {
  const parts = splitLitigationCounterSection(raw, [LITIGATION_COUNTERCLAIM_MARKER]);
  if (!parts) {
    const text = String(raw || '').replace(/\r\n/g, '\n').trim();
    return {claim: text.slice(0, 2000), counterclaim: ''};
  }
  return {claim: parts.primary.slice(0, 2000), counterclaim: parts.counter.slice(0, 2000)};
}

/** 写入台账单元格时，反诉判决/裁决结果与本诉结果之间的分隔。 */
const LITIGATION_COUNTER_RESULT_MARKER = '反诉结果：';

/**
 * 判决/裁决结果一行的名称。执行案件只有执行结果。
 * @param item 案件类型信息
 */
export function litigationJudgmentResultLabels(item: Pick<LitigationCase, 'disputeType' | 'instance' | 'enforcementStatus'>): {primary: string; counter: string; addTitle: string; removeTitle: string; single: boolean} {
  if (deriveLitigationNumberingKind(item) === 'zx') {
    return {primary: '执行结果', counter: '', addTitle: '', removeTitle: '', single: true};
  }
  return {primary: '本诉结果', counter: '反诉结果', addTitle: '添加反诉判决/裁决结果', removeTitle: '删除反诉结果', single: false};
}

/**
 * 判决/裁决结果列在 Excel 里的单元格文本。执行案件只写执行结果。
 * @param item 案件
 */
export function formatLitigationResultCell(item: Pick<LitigationCase, 'resultSummary' | 'disputeType' | 'instance' | 'enforcementStatus'> & {counterclaimResult?: string}): string {
  const primary = String(item.resultSummary || '').trim();
  if (litigationJudgmentResultLabels(item).single) return primary;
  const counter = String(item.counterclaimResult || '').trim();
  if (!counter) return primary;
  return primary ? `${primary}\n${LITIGATION_COUNTER_RESULT_MARKER}${counter}` : `${LITIGATION_COUNTER_RESULT_MARKER}${counter}`;
}

/**
 * 把判决/裁决结果单元格拆回本诉结果和反诉结果。
 * @param raw 单元格文本
 */
export function splitLitigationResultCell(raw: string): {primary: string; counter: string} {
  const parts = splitLitigationCounterSection(raw, [LITIGATION_COUNTER_RESULT_MARKER]);
  if (!parts) {
    const text = String(raw || '').replace(/\r\n/g, '\n').trim();
    return {primary: text.slice(0, 2000), counter: ''};
  }
  return {primary: parts.primary.slice(0, 2000), counter: parts.counter.slice(0, 2000)};
}

/** 写入台账单元格时，反诉或反请求支持金额与本诉支持金额之间的分隔。 */
const LITIGATION_COUNTER_AWARDED_MARKERS = ['反诉获支持金额：', '反请求获支持金额：', '反诉或支持金额：', '反请求或支持金额：', '反诉（反请求）获支持金额：'] as const;

/**
 * 判决（裁决）支持金额一行的名称。诉讼用本诉/反诉，仲裁和劳动仲裁用本请求/反请求，执行只有执行到位的金额。
 * @param item 案件类型信息
 */
export function litigationAwardedAmountLabels(
  item: Pick<LitigationCase, 'disputeType' | 'instance' | 'enforcementStatus'>,
  rowLabels?: {primary?: string; counter?: string},
): {primary: string; counter: string; addTitle: string; removeTitle: string; single: boolean} {
  const kind = deriveLitigationNumberingKind(item);
  if (kind === 'zx') {
    return {primary: '执行到位的金额', counter: '', addTitle: '', removeTitle: '', single: true};
  }
  if (kind === 'zc' || kind === 'ldzc') {
    return {
      primary: '本请求获支持金额',
      counter: '反请求获支持金额',
      addTitle: '添加反请求获支持金额',
      removeTitle: '删除反请求获支持金额',
      single: false,
    };
  }
  const primary = rowLabels?.primary?.trim().slice(0, 20) || '本诉获支持金额';
  const counter = rowLabels?.counter?.trim().slice(0, 20) || '反诉获支持金额';
  return {
    primary,
    counter,
    addTitle: `添加${counter}`,
    removeTitle: `删除${counter}`,
    single: false,
  };
}

/**
 * 判决（裁决）支持金额列在 Excel 里的单元格文本。执行案件只写执行到位的金额。
 * @param item 案件
 */
export function formatLitigationAwardedAmountCell(item: Pick<LitigationCase, 'disputeType' | 'instance' | 'enforcementStatus'> & {awardedAmount?: number; counterclaimAwardedAmount?: number}): string {
  const primary = item.awardedAmount && item.awardedAmount > 0 ? String(item.awardedAmount) : '';
  if (litigationAwardedAmountLabels(item).single) return primary;
  const counter = item.counterclaimAwardedAmount && item.counterclaimAwardedAmount > 0 ? String(item.counterclaimAwardedAmount) : '';
  if (!counter) return primary;
  const marker = deriveLitigationNumberingKind(item) === 'ss' ? '反诉获支持金额：' : '反请求获支持金额：';
  return primary ? `${primary}\n${marker}${counter}` : `${marker}${counter}`;
}

/**
 * 把支持金额单元格拆回本诉获支持金额和反诉获支持金额。
 * @param raw 单元格文本
 */
export function splitLitigationAwardedAmountCell(raw: string): {primary: number; counter: number} {
  const parts = splitLitigationCounterSection(raw, LITIGATION_COUNTER_AWARDED_MARKERS);
  if (parts) {
    return {
      primary: parseLitigationMoney(parts.primary) ?? 0,
      counter: parseLitigationMoney(parts.counter) ?? 0,
    };
  }
  return splitLitigationMoneyPair(raw) || {primary: parseLitigationMoney(String(raw || '')) ?? 0, counter: 0};
}

/** 案涉金额里，反诉或反请求金额与本诉金额之间的分隔。 */
const LITIGATION_COUNTER_AMOUNT_MARKERS = ['反诉金额：', '反请求金额：', '反诉（反请求）金额：'] as const;

/**
 * 案涉金额一行的名称。诉讼用本诉/反诉，仲裁和劳动仲裁用本请求/反请求，执行只有申请执行标的金额。
 * @param item 案件类型信息
 */
export function litigationInvolvedAmountLabels(
  item: Pick<LitigationCase, 'disputeType' | 'instance' | 'enforcementStatus'>,
): {primary: string; counter: string; addTitle: string; single: boolean} {
  const kind = deriveLitigationNumberingKind(item);
  if (kind === 'zx') return {primary: '申请执行标的金额', counter: '', addTitle: '', single: true};
  if (kind === 'zc' || kind === 'ldzc') return {primary: '本请求金额', counter: '反请求金额', addTitle: '添加反请求金额', single: false};
  return {primary: '本诉金额', counter: '反诉金额', addTitle: '添加反诉金额', single: false};
}

/**
 * 把用户填写的金额收成非负数字。空值视为 0，无法识别时返回 null。
 * @param raw 单元格或输入框文本
 */
export function parseLitigationMoney(raw: string): number | null {
  const text = String(raw || '').replace(/,/g, '').replace(/元/g, '').trim().replace(/\.$/, '');
  if (!text) return 0;
  if (!/^\d+(\.\d+)?$/.test(text)) return null;
  const value = Number(text);
  return Number.isFinite(value) && value >= 0 ? value : null;
}

/**
 * 案涉金额列在 Excel 里的单元格文本。
 * 执行案件只写申请执行标的金额；其余案件在有反诉或反请求金额时另起一行。
 * @param item 案件
 */
export function formatLitigationInvolvedAmountCell(
  item: Pick<LitigationCase, 'disputeType' | 'instance' | 'enforcementStatus'> & {principalAmount?: number; counterclaimAmount?: number},
): string {
  const principal = item.principalAmount && item.principalAmount > 0 ? String(item.principalAmount) : '';
  if (litigationInvolvedAmountLabels(item).single) return principal;
  const counter = item.counterclaimAmount && item.counterclaimAmount > 0 ? String(item.counterclaimAmount) : '';
  if (!counter) return principal;
  const marker = deriveLitigationNumberingKind(item) === 'ss' ? '反诉金额：' : '反请求金额：';
  return principal ? `${principal}\n${marker}${counter}` : `${marker}${counter}`;
}

/**
 * 把案涉金额单元格拆回本诉金额和反诉金额。
 * @param raw 单元格文本
 */
export function splitLitigationInvolvedAmountCell(raw: string): {principal: number; counterclaim: number} {
  const parts = splitLitigationCounterSection(raw, LITIGATION_COUNTER_AMOUNT_MARKERS);
  if (parts) {
    return {
      principal: parseLitigationMoney(parts.primary) ?? 0,
      counterclaim: parseLitigationMoney(parts.counter) ?? 0,
    };
  }
  const pair = splitLitigationMoneyPair(raw);
  if (pair) return {principal: pair.primary, counterclaim: pair.counter};
  return {principal: parseLitigationMoney(String(raw || '')) ?? 0, counterclaim: 0};
}

/**
 * 新建时一律按方案自动生成内部案号。
 * @param options 规则、已占用案号、立案信息
 */
export function resolveLitigationInternalNo(options: {
  rule?: LitigationInternalNoRule;
  existing: string[];
  context?: LitigationInternalNoContext;
}): {internalNo: string; error?: string} {
  const rule = normalizeLitigationInternalNoRule(options.rule);
  const context = options.context || {};
  const taken = new Set(options.existing.map((item) => item.trim().toLocaleLowerCase()).filter(Boolean));
  let serial = 1;
  const key = identityKey(rule, context);
  for (const value of options.existing) {
    const parsed = parseInternalNoSerial(rule, value, context);
    if (parsed && parsed.key === key) serial = Math.max(serial, parsed.serial + 1);
  }
  let next = formatLitigationInternalNo(rule, context, serial);
  let guard = 0;
  while (taken.has(next.toLocaleLowerCase()) && guard < 1000) {
    serial += 1;
    next = formatLitigationInternalNo(rule, context, serial);
    guard += 1;
  }
  if (!next) return {internalNo: '', error: '内部案号规则无法生成有效编号，请至少启用编号段'};
  return {internalNo: next};
}

/**
 * 内部案号填写说明，随方案规则变化。
 * @param rule 方案规则
 */
export function litigationInternalNoFillGuide(rule?: LitigationInternalNoRule): LitigationFieldFillGuide {
  const normalized = normalizeLitigationInternalNoRule(rule);
  const example = previewLitigationInternalNo(normalized);
  const enabled = normalized.parts.filter((part) => part.enabled).map((part) => LITIGATION_INTERNAL_NO_PART_LABELS[part.kind]).join('、');
  const pattern = `当前方案样式：${example}（启用：${enabled || '编号'}，流水 ${normalized.serialWidth} 位）。`;
  const note = normalized.note;
  return {
    rule: `${note ? `${note} ` : ''}由系统按本方案自动生成，办案人员不用手填。${pattern}申请立案日和案件类型都选定之后才取号，缺一项就不占号。同年份、同单位、同类型的流水号依次递增。本台账主键，同一台账内不可重复；四张表必须完全一致。不要用法院/仲裁案号代替。年份取申请立案日的年份，申请立案日改变时年份跟着改。案件类型改变时，编号中的类型代号会改成对应代号。取号后不要手改。`,
    example,
  };
}

function defaultInternalNoParts(): LitigationInternalNoPart[] {
  return LITIGATION_INTERNAL_NO_PART_KINDS.map((kind) => ({
    kind,
    enabled: kind === 'year' || kind === 'caseType' || kind === 'serial',
  }));
}

function migrateLegacyInternalNoParts(record: Record<string, unknown>, orgCode: string): LitigationInternalNoPart[] {
  const includeYear = record.includeYear !== false;
  const ordered: LitigationInternalNoPartKind[] = [];
  if (orgCode) ordered.push('orgCode');
  if (includeYear) ordered.push('year');
  ordered.push('serial');
  for (const kind of LITIGATION_INTERNAL_NO_PART_KINDS) {
    if (!ordered.includes(kind)) ordered.push(kind);
  }
  return ordered.map((kind) => ({
    kind,
    enabled: kind === 'serial' || (kind === 'year' && includeYear) || (kind === 'orgCode' && Boolean(orgCode)),
  }));
}

function normalizeInternalNoParts(value: unknown[]): LitigationInternalNoPart[] {
  const seen = new Set<LitigationInternalNoPartKind>();
  const ordered: LitigationInternalNoPart[] = [];
  for (const item of value) {
    const record = item && typeof item === 'object' ? item as Record<string, unknown> : {};
    const kind = String(record.kind || '') as LitigationInternalNoPartKind;
    if (!LITIGATION_INTERNAL_NO_PART_KINDS.includes(kind) || seen.has(kind)) continue;
    seen.add(kind);
    ordered.push({kind, enabled: kind === 'serial' ? true : record.enabled !== false});
  }
  for (const kind of LITIGATION_INTERNAL_NO_PART_KINDS) {
    if (seen.has(kind)) continue;
    ordered.push({kind, enabled: kind === 'serial'});
  }
  return ordered;
}

function normalizeCaseTypeCodes(value: unknown): Record<LitigationNumberingKind, string> {
  const record = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  return {
    ss: sanitizeTypeCode(record.ss, 'ss'),
    zx: sanitizeTypeCode(record.zx, 'zx'),
    zc: sanitizeTypeCode(record.zc, 'zc'),
    ldzc: sanitizeTypeCode(record.ldzc, 'ldzc'),
  };
}

/**
 * 单位代号保留方案里填写的文字，只去掉分隔符，避免和案号分段粘在一起。
 * @param value 单位代码
 * @param separator 案号分隔符
 */
function sanitizeOrgCode(value: string, separator = ''): string {
  let text = value.trim().slice(0, 40);
  if (separator) text = text.split(separator).join('');
  return text;
}

function sanitizeTypeCode(value: unknown, fallback: string): string {
  const text = String(value ?? '').trim().replace(/[^A-Za-z0-9]/g, '').slice(0, 12);
  return text || fallback;
}

function sanitizeOtherText(value: string, separator: string): string {
  let text = value.trim().slice(0, 20);
  if (separator) text = text.split(separator).join('');
  return text;
}

function filingDateParts(date?: string): {year: string; month: string; day: string} {
  const match = String(date || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return {year: match[1], month: match[2], day: match[3]};
  const now = new Date();
  return {
    year: String(now.getFullYear()),
    month: String(now.getMonth() + 1).padStart(2, '0'),
    day: String(now.getDate()).padStart(2, '0'),
  };
}

/**
 * 本次编号实际使用的单位代号。调用方传入时以传入值为准。
 * @param rule 编号规则
 * @param context 立案信息
 */
function orgCodeToken(rule: LitigationInternalNoRule, context: LitigationInternalNoContext): string {
  const source = context.orgCode !== undefined ? context.orgCode : rule.orgCode;
  return sanitizeOrgCode(String(source || ''), rule.separator);
}

function outputKinds(rule: LitigationInternalNoRule, context: LitigationInternalNoContext = {}): LitigationInternalNoPartKind[] {
  return rule.parts
    .filter((part) => {
      if (!part.enabled) return false;
      if (part.kind === 'orgCode') return Boolean(orgCodeToken(rule, context));
      if (part.kind === 'other') return Boolean(rule.otherText);
      return true;
    })
    .map((part) => part.kind);
}

function tokenForKind(
  kind: LitigationInternalNoPartKind,
  rule: LitigationInternalNoRule,
  context: LitigationInternalNoContext,
  serial: number,
): string {
  const date = filingDateParts(context.filingDate);
  if (kind === 'year') return date.year;
  if (kind === 'month') return date.month;
  if (kind === 'day') return date.day;
  if (kind === 'orgCode') return orgCodeToken(rule, context);
  if (kind === 'caseType') return rule.caseTypeCodes[deriveLitigationNumberingKind(context)];
  if (kind === 'other') return rule.otherText;
  return String(Math.max(1, serial)).padStart(rule.serialWidth, '0');
}

function identityKey(rule: LitigationInternalNoRule, context: LitigationInternalNoContext): string {
  return outputKinds(rule, context)
    .filter((kind) => kind !== 'serial')
    .map((kind) => tokenForKind(kind, rule, context, 0))
    .join('\u0001');
}

/**
 * 把符合当前规则的自动案号拆成各段。类型代号只要是方案里配置过的代号即可，便于改类型后替换。
 * @param rule 编号规则
 * @param text 案号
 * @param context 立案信息
 */
function autoInternalNoTokens(
  rule: LitigationInternalNoRule,
  text: string,
  context: LitigationInternalNoContext,
): string[] | null {
  const kinds = outputKinds(rule, context);
  if (!kinds.includes('caseType') || !kinds.includes('serial')) return null;
  const tokens = rule.separator ? text.split(rule.separator) : consumeTokensWithoutSeparator(rule, kinds, text, context);
  if (!tokens || tokens.length !== kinds.length || tokens.some((token) => !token)) return null;
  const knownTypes = new Set(Object.values(rule.caseTypeCodes).map((code) => code.toLocaleLowerCase()));
  for (let index = 0; index < kinds.length; index += 1) {
    const kind = kinds[index];
    const token = tokens[index];
    if (kind === 'year' && !/^\d{4}$/.test(token)) return null;
    if ((kind === 'month' || kind === 'day') && !/^\d{2}$/.test(token)) return null;
    if (kind === 'serial' && !/^\d+$/.test(token)) return null;
    if (kind === 'caseType' && !knownTypes.has(token.toLocaleLowerCase())) return null;
    if (kind === 'orgCode' && token.toLocaleLowerCase() !== orgCodeToken(rule, context).toLocaleLowerCase()) return null;
    if (kind === 'other' && token !== rule.otherText) return null;
  }
  return tokens;
}

function parseInternalNoSerial(rule: LitigationInternalNoRule, value: string, context: LitigationInternalNoContext = {}): {serial: number; key: string} | null {
  const kinds = outputKinds(rule, context);
  const serialIndex = kinds.indexOf('serial');
  if (serialIndex < 0) return null;
  const text = value.trim();
  if (!text) return null;
  const tokens = rule.separator ? text.split(rule.separator) : consumeTokensWithoutSeparator(rule, kinds, text, context);
  if (!tokens || tokens.length !== kinds.length) return null;
  const serial = Number(tokens[serialIndex]);
  if (!Number.isFinite(serial) || serial < 1) return null;
  return {serial, key: tokens.filter((_, index) => index !== serialIndex).join('\u0001')};
}

function consumeTokensWithoutSeparator(
  rule: LitigationInternalNoRule,
  kinds: LitigationInternalNoPartKind[],
  text: string,
  context: LitigationInternalNoContext = {},
): string[] | null {
  let rest = text;
  const tokens: string[] = [];
  for (const kind of kinds) {
    if (kind === 'year') {
      const match = rest.match(/^(\d{4})/);
      if (!match) return null;
      tokens.push(match[1]);
      rest = rest.slice(4);
      continue;
    }
    if (kind === 'month' || kind === 'day') {
      const match = rest.match(/^(\d{2})/);
      if (!match) return null;
      tokens.push(match[1]);
      rest = rest.slice(2);
      continue;
    }
    if (kind === 'orgCode') {
      const orgCode = orgCodeToken(rule, context);
      if (!orgCode || !rest.startsWith(orgCode)) return null;
      tokens.push(orgCode);
      rest = rest.slice(orgCode.length);
      continue;
    }
    if (kind === 'other') {
      if (!rule.otherText || !rest.startsWith(rule.otherText)) return null;
      tokens.push(rule.otherText);
      rest = rest.slice(rule.otherText.length);
      continue;
    }
    if (kind === 'caseType') {
      const codes = Object.values(rule.caseTypeCodes).sort((left, right) => right.length - left.length);
      const hit = codes.find((code) => rest.startsWith(code));
      if (!hit) return null;
      tokens.push(hit);
      rest = rest.slice(hit.length);
      continue;
    }
    const match = rest.match(/^(\d{1,6})/);
    if (!match) return null;
    tokens.push(match[1]);
    rest = rest.slice(match[1].length);
  }
  return rest ? null : tokens;
}

const fieldById = new Map(
  [...LITIGATION_PLAN_STANDARD_FIELDS, ...LITIGATION_DETAIL_FIELDS].map((item) => [item.id, item]),
);

/**
 * 默认导出映射（按系统固定列顺序）。
 */
export function defaultLitigationExportFields(): LitigationPlanFieldMapping[] {
  return LITIGATION_PLAN_STANDARD_FIELDS.map((field, index) => ({
    enabled: true,
    columnIndex: index,
    columnLetter: columnLetter(index),
    header: field.label,
    sourceField: field.id,
    valueMode: 'source',
  }));
}

/**
 * 系统标准四表模板某一工作表的表头行（与后台字段目录 label 一致）。
 * @param sheetKind 工作表类型
 */
export function platformLitigationTemplateSheetHeaders(sheetKind: LitigationSheetKind): string[] {
  const spec = LITIGATION_DEFAULT_SHEETS.find((item) => item.kind === sheetKind);
  if (!spec) return [];
  return spec.fieldIds.map((fieldId) => (
    sheetKind === 'parties' && fieldId === 'internalNo' ? '案件内部编号' : litigationPlanFieldLabel(fieldId)
  ));
}

type InspectedLitigationColumn = {
  columnIndex: number;
  columnLetter: string;
  header: string;
};

/**
 * 将方案列映射补全为系统标准字段目录顺序（避免旧 Excel 仅识别出部分列时列表缺字段）。
 *
 * 重要：`header` 一律保留 Excel 里的真实表头文案，绝不用标准字段名覆盖——
 * 否则用户上传模板后，客户端预览与导出表头会变成系统字段名，与用户的表格不一致。
 * 标准字段名只作为「未识别列」的兜底显示。
 *
 * @param sheetKind 工作表类型
 * @param exportFields 已有映射
 * @param inspectedColumns 模板识别列（可选，用于对齐 Excel 列字母）
 */
export function mergeLitigationSheetExportFieldsWithCatalog(
  sheetKind: LitigationSheetKind,
  exportFields: LitigationPlanFieldMapping[],
  inspectedColumns: InspectedLitigationColumn[] = [],
): LitigationPlanFieldMapping[] {
  const spec = LITIGATION_DEFAULT_SHEETS.find((item) => item.kind === sheetKind);
  const defaults = defaultLitigationSheetMaps(LITIGATION_PLAN_STANDARD_FIELDS).find((item) => item.sheetKind === sheetKind);
  if (!spec || !defaults) return exportFields;
  const bySource = new Map(exportFields.filter((item) => item.sourceField).map((item) => [item.sourceField, item]));
  const columnByField = new Map<string, InspectedLitigationColumn>();
  for (const column of inspectedColumns) {
    const fieldId = suggestLitigationPlanField(column.header, sheetKind);
    if (fieldId && !columnByField.has(fieldId)) columnByField.set(fieldId, column);
  }
  return spec.fieldIds.map((fieldId, index) => {
    const prior = bySource.get(fieldId);
    const def = defaults.exportFields.find((item) => item.sourceField === fieldId);
    // 只按「字段识别结果」取列；不再用数组下标兜底，避免未识别列造成整体错位。
    const column = columnByField.get(fieldId);
    const excelHeader = column?.header?.trim() || prior?.header?.trim() || '';
    const merged = normalizeLitigationPlanFieldMapping({
      ...(def ?? {}),
      ...(prior ?? {}),
      sourceField: fieldId,
      // 真实表头优先；没有真实表头时才退回标准字段名
      header: excelHeader || litigationPlanFieldLabel(fieldId),
      columnIndex: column?.columnIndex ?? prior?.columnIndex ?? def?.columnIndex ?? index,
      columnLetter: column?.columnLetter ?? prior?.columnLetter ?? def?.columnLetter ?? columnLetter(index),
      enabled: prior?.enabled ?? def?.enabled ?? Boolean(column),
    });
    return merged ?? {
      enabled: Boolean(def?.enabled),
      columnIndex: index,
      columnLetter: columnLetter(index),
      header: excelHeader || litigationPlanFieldLabel(fieldId),
      sourceField: fieldId,
      valueMode: 'source' as const,
    };
  });
}

/**
 * 规范化方案列映射，保留取值处理与计算公式。
 * @param item 原始映射
 */
export function normalizeLitigationPlanFieldMapping(item: unknown): LitigationPlanFieldMapping | null {
  const record = item && typeof item === 'object' ? item as Record<string, unknown> : {};
  const sourceField = String(record.sourceField ?? '');
  const valueMode = record.valueMode === 'formula' || record.valueMode === 'reference' ? record.valueMode : 'source';
  const valueTransform = record.valueTransform === 'left' || record.valueTransform === 'right' || record.valueTransform === 'prefix' || record.valueTransform === 'suffix'
    ? record.valueTransform
    : '';
  const mapping: LitigationPlanFieldMapping = {
    enabled: record.enabled !== false,
    columnIndex: Math.max(0, Number(record.columnIndex) || 0),
    columnLetter: String(record.columnLetter ?? ''),
    header: String(record.header ?? '').trim().slice(0, 120),
    sourceField: fieldById.has(sourceField) ? sourceField : '',
    sourceModuleId: String(record.sourceModuleId ?? '').trim().slice(0, 80),
    sourceModuleVersion: String(record.sourceModuleVersion ?? '').trim().slice(0, 20),
    valueMode,
    valueTransform,
    transformLength: Math.max(1, Math.min(100, Number(record.transformLength) || 3)),
    transformText: String(record.transformText ?? '').slice(0, 120),
    calculationExpression: String(record.calculationExpression ?? '').trim().slice(0, 500),
    calculationDecimalPlaces: Math.max(0, Math.min(8, Number(record.calculationDecimalPlaces) || 0)),
    calculationMissingValuePolicy: record.calculationMissingValuePolicy === 'zero' ? 'zero' : 'error',
    referenceSheetName: String(record.referenceSheetName ?? '').trim().slice(0, 120),
    referenceColumnIndex: Number.isInteger(Number(record.referenceColumnIndex)) ? Number(record.referenceColumnIndex) : undefined,
    referenceColumnLetter: String(record.referenceColumnLetter ?? '').trim().slice(0, 8),
    referenceHeader: String(record.referenceHeader ?? '').trim().slice(0, 120),
  };
  if (!mapping.header && !mapping.sourceField && valueMode === 'source') return null;
  return mapping;
}

/**
 * 创建组织默认诉讼台账方案。
 * @param companyName 所属名称
 */
export function createDefaultLitigationLedgerPlan(companyName: string): LitigationLedgerPlan {
  const now = new Date().toISOString();
  const sheetMaps = defaultLitigationSheetMaps(LITIGATION_PLAN_STANDARD_FIELDS);
  const casesMap = sheetMaps.find((item) => item.sheetKind === 'cases');
  return {
    id: `llplan_${cryptoRandom()}`,
    name: '默认诉讼台账方案',
    companyName: companyName.trim().slice(0, 200),
    templateFileId: '',
    fileName: '',
    sheetName: casesMap?.sheetName || '案件总表',
    headerRow: 1,
    dataStartRow: 2,
    listFieldIds: (casesMap?.exportFields ?? [])
      .filter((item) => item.enabled !== false && item.sourceField)
      .map((item) => item.sourceField),
    exportFields: casesMap?.exportFields ?? defaultLitigationExportFields(),
    sheetMaps,
    internalNoRule: defaultLitigationInternalNoRule(),
    procedureTemplates: {},
    createdAt: now,
    updatedAt: now,
  };
}

function cryptoRandom(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function columnLetter(index: number): string {
  let value = index;
  let letters = '';
  while (value >= 0) {
    letters = String.fromCharCode((value % 26) + 65) + letters;
    value = Math.floor(value / 26) - 1;
  }
  return letters;
}

function normalizeKey(value: string): string {
  return value.replace(/\s+/g, '').replace(/[：:（）()【】\[\]·\-_/]/g, '').toLowerCase();
}

/**
 * 旧模板常把「案件名称」列误映射为 title（案由），导致后台填写规则绑在一起。
 * @param fields 工作表列映射
 */
const DEDICATED_OUR_PARTY_HEADERS = new Set(['我方', '我方主体']);
const PLAINTIFF_ROLE_HEADERS = new Set(['原告', '申请人', '原告/执行申请人', '原告/申请人', '执行申请人', '原告/上诉人/申请人']);
const COMBINED_PLAINTIFF_HEADERS = new Set(['原告/上诉人/申请人']);
const DEFENDANT_HEADERS = new Set(['对方', '被告', '被申请人', '对方当事人', '被告/被执行人', '被执行人', '被告/被申请人', '被告/被上诉人/被申请人']);
const THIRD_PARTY_HEADERS = new Set(['第三人', '有独立请求权第三人', '无独立请求权第三人']);
const RELATED_COURT_NO_HEADERS = new Set(['诉讼与执行关联案号', '关联案号']);

/**
 * 压缩表头，便于和固定列名比较。
 * @param header 表头
 */
function compactLitigationHeader(header: string): string {
  return String(header ?? '').replace(/\s+/g, '').replace(/／/g, '/');
}

const LEGACY_PARTY_SHEET_FIELDS = new Set(['partyRole', 'partyName', 'partyPrimary', 'partyContact']);

/**
 * 把仍按当事人分行的「案件背景」表换成与背景窗口一致的三列。
 * 已经有内容介绍列时保持原样，避免盖掉管理员后来改过的表头。
 * @param fields 案件背景表列映射
 */
export function healLitigationBackgroundSheetMappings(fields: LitigationPlanFieldMapping[]): LitigationPlanFieldMapping[] {
  if (fields.some((field) => field.enabled !== false && field.sourceField === 'caseBackground')) return fields;
  const legacy = fields.some((field) => field.sourceField && LEGACY_PARTY_SHEET_FIELDS.has(field.sourceField));
  if (!legacy) return fields;
  const internalHeader = fields.find((field) => field.sourceField === 'internalNo')?.header?.trim() || '';
  const keepInternalHeader = internalHeader && internalHeader !== '内部案号' && internalHeader !== '内部编号';
  return [
    {enabled: true, columnIndex: 0, columnLetter: 'A', header: '案件名称', sourceField: 'caseName', valueMode: 'source'},
    {enabled: true, columnIndex: 1, columnLetter: 'B', header: keepInternalHeader ? internalHeader : '案件内部编号', sourceField: 'internalNo', valueMode: 'source'},
    {enabled: true, columnIndex: 2, columnLetter: 'C', header: '内容介绍', sourceField: 'caseBackground', valueMode: 'source'},
  ];
}

/**
 * 同一张表已经有「我方」时，原告列单独填写。
 * 已经有被告列时，第三人列单独填写。
 * 只有一列旧表时，仍沿用原来的我方或对方。
 * @param fields 工作表列映射
 */
export function healLitigationOurPartyColumnMappings(fields: LitigationPlanFieldMapping[]): LitigationPlanFieldMapping[] {
  const hasDedicatedOur = fields.some((field) => (
    field.enabled !== false
    && field.sourceField === 'ourParty'
    && DEDICATED_OUR_PARTY_HEADERS.has(compactLitigationHeader(field.header))
  ));
  const hasDedicatedOpponent = fields.some((field) => (
    field.enabled !== false
    && field.sourceField === 'opponent'
    && DEFENDANT_HEADERS.has(compactLitigationHeader(field.header))
  ));
  return fields.map((field) => {
    if (field.enabled === false) return field;
    const header = compactLitigationHeader(field.header);
    if (COMBINED_PLAINTIFF_HEADERS.has(header) && field.sourceField !== 'plaintiffParty') {
      return {...field, enabled: true, sourceField: 'plaintiffParty'};
    }
    if (hasDedicatedOur && PLAINTIFF_ROLE_HEADERS.has(header) && (field.sourceField === 'ourParty' || field.sourceField === '')) {
      return {...field, enabled: true, sourceField: 'plaintiffParty'};
    }
    if (hasDedicatedOpponent && THIRD_PARTY_HEADERS.has(header) && (field.sourceField === 'opponent' || field.sourceField === '')) {
      return {...field, enabled: true, sourceField: 'thirdParty'};
    }
    return field;
  });
}

const FILING_APPLICATION_DATE_HEADERS = new Set(['申请立案日', '申请立案日期']);
const CLAIM_COLUMN_HEADERS = new Set(['诉讼请求/申请执行标的', '申请执行标的']);

/**
 * 表头为「申请立案日」时，这一列写入立案日，内部案号的年份取它。
 * @param fields 工作表列映射
 */
export function healLitigationFilingDateColumnMappings(fields: LitigationPlanFieldMapping[]): LitigationPlanFieldMapping[] {
  return fields.map((field) => {
    if (field.enabled === false) return field;
    if (!FILING_APPLICATION_DATE_HEADERS.has(compactLitigationHeader(field.header))) return field;
    if (field.sourceField === 'filingDate') return field;
    return {...field, enabled: true, sourceField: 'filingDate'};
  });
}

const JUDGMENT_RESULT_HEADERS = new Set(['判决支持标的金额/执行到位金额', '判决支持的金额/执行到位金额', '判决支持标的金额', '执行到位金额', '结果摘要', '裁判结果', '裁决结果']);

/**
 * 原「判决支持标的金额/执行到位金额」列改为判决/裁决结果。
 * @param fields 工作表列映射
 */
export function healLitigationResultColumnMappings(fields: LitigationPlanFieldMapping[]): LitigationPlanFieldMapping[] {
  return fields.map((field) => {
    const header = compactLitigationHeader(field.header);
    if (field.sourceField === 'resultSummary') {
      if (!header || JUDGMENT_RESULT_HEADERS.has(header)) {
        return header === '判决/裁决结果' ? field : {...field, enabled: field.enabled !== false, header: '判决/裁决结果'};
      }
      return field;
    }
    if (!JUDGMENT_RESULT_HEADERS.has(header)) return field;
    if (field.sourceField && field.sourceField !== 'resultSummary' && field.sourceField !== 'amount' && field.sourceField !== '') return field;
    return {...field, enabled: field.enabled !== false, sourceField: 'resultSummary', header: '判决/裁决结果'};
  });
}

/**
 * 同一张表只保留列号最靠前的「判决/裁决结果」。后面重复映射的同名字段删掉。
 * @param fields 工作表列映射
 */
export function dropDuplicateLitigationResultColumns(fields: LitigationPlanFieldMapping[]): LitigationPlanFieldMapping[] {
  const enabled = fields.filter((field) => field.enabled !== false && field.sourceField === 'resultSummary');
  if (enabled.length < 2) return fields;
  const keepIndex = enabled.reduce((best, field) => (field.columnIndex < best ? field.columnIndex : best), enabled[0]!.columnIndex);
  return fields.filter((field) => field.enabled === false || field.sourceField !== 'resultSummary' || field.columnIndex === keepIndex);
}

const AWARDED_AMOUNT_HEADER = '判决（裁决）支持的金额/执行到位的金额';
const AWARDED_AMOUNT_HEADERS = new Set([
  AWARDED_AMOUNT_HEADER,
  '判决（裁决）支持的金额',
  '执行到位的金额',
  '非货币判决结果/非货币执行结果',
  '非货币判决结果',
  '非货币执行结果',
  '非货币结果',
  '非金钱判决结果',
]);

/**
 * 原「非货币判决结果/非货币执行结果」列改为判决（裁决）支持的金额。
 * @param fields 工作表列映射
 */
export function healLitigationAwardedAmountColumnMappings(fields: LitigationPlanFieldMapping[]): LitigationPlanFieldMapping[] {
  return fields.map((field) => {
    const header = compactLitigationHeader(field.header);
    if (field.sourceField === 'awardedAmount') {
      if (!header || (AWARDED_AMOUNT_HEADERS.has(header) && header !== AWARDED_AMOUNT_HEADER)) {
        return {...field, enabled: field.enabled !== false, header: AWARDED_AMOUNT_HEADER};
      }
      return field;
    }
    if (field.sourceField !== 'nonMonetaryResult' && !AWARDED_AMOUNT_HEADERS.has(header)) return field;
    if (field.sourceField && field.sourceField !== 'nonMonetaryResult' && field.sourceField !== 'awardedAmount' && !AWARDED_AMOUNT_HEADERS.has(header)) return field;
    return {...field, enabled: field.enabled !== false, sourceField: 'awardedAmount', header: AWARDED_AMOUNT_HEADER};
  });
}

/**
 * 这一列原先是非货币结果。改成支持金额后，格子里已经是数字的内容留在本诉获支持金额里。
 * @param cases 按旧映射读出的案件
 * @param before 旧台账映射
 * @param after 新台账映射
 */
export function carryNonMonetaryTextIntoAwardedAmount(
  cases: Array<{nonMonetaryResult?: string; awardedAmount?: number}>,
  before: Pick<LitigationLedgerPlan, 'sheetMaps'>,
  after: Pick<LitigationLedgerPlan, 'sheetMaps'>,
): void {
  const moved = (before.sheetMaps ?? []).some((map) => (map.exportFields ?? []).some((field) => {
    if (field.enabled === false || field.sourceField !== 'nonMonetaryResult') return false;
    return (after.sheetMaps ?? []).some((next) => next.sheetName === map.sheetName && (next.exportFields ?? []).some((item) =>
      item.columnIndex === field.columnIndex && item.sourceField === 'awardedAmount'));
  }));
  if (!moved) return;
  for (const item of cases) {
    if (item.awardedAmount) continue;
    const parsed = parseLitigationMoney(String(item.nonMonetaryResult || ''));
    if (!parsed) continue;
    item.awardedAmount = parsed;
  }
}

/**
 * 这一列原先写成标的额。改成判决/裁决结果后，格子里已有的数字留在本诉结果里。
 * @param cases 按旧映射读出的案件
 * @param before 旧台账映射
 * @param after 新台账映射
 */
export function carryJudgmentAmountIntoResult(
  cases: Array<{amount?: number; resultSummary?: string}>,
  before: Pick<LitigationLedgerPlan, 'sheetMaps'>,
  after: Pick<LitigationLedgerPlan, 'sheetMaps'>,
): void {
  const moved = (before.sheetMaps ?? []).some((map) => (map.exportFields ?? []).some((field) => {
    if (field.enabled === false || field.sourceField !== 'amount') return false;
    if (!JUDGMENT_RESULT_HEADERS.has(compactLitigationHeader(field.header))) return false;
    return (after.sheetMaps ?? []).some((next) => next.sheetName === map.sheetName && (next.exportFields ?? []).some((item) =>
      item.columnIndex === field.columnIndex && item.sourceField === 'resultSummary'));
  }));
  if (!moved) return;
  for (const item of cases) {
    if (String(item.resultSummary ?? '').trim()) continue;
    const amount = Number(item.amount);
    if (!Number.isFinite(amount) || amount === 0) continue;
    item.resultSummary = String(amount);
  }
}

const CLOSED_FLAG_COLUMN_HEADERS = new Set(['是否结案', '是否已结案', '结案状态']);

/**
 * 原「是否结案」列改为案涉金额。结案改由当前阶段「已结案」决定。
 * @param fields 工作表列映射
 */
export function healLitigationInvolvedAmountColumnMappings(fields: LitigationPlanFieldMapping[]): LitigationPlanFieldMapping[] {
  return fields.map((field) => {
    const header = compactLitigationHeader(field.header);
    if (field.sourceField === 'involvedAmount') {
      if (!header || CLOSED_FLAG_COLUMN_HEADERS.has(header)) {
        return header === '案涉金额' ? field : {...field, header: '案涉金额'};
      }
      return field;
    }
    if (field.sourceField !== 'closedFlag' && !CLOSED_FLAG_COLUMN_HEADERS.has(header)) return field;
    return {...field, sourceField: 'involvedAmount', header: '案涉金额'};
  });
}

/**
 * 表头为「诉讼请求/申请执行标的」时，写入诉讼请求。
 * 执行案件在这一格只显示申请执行标的。
 * @param fields 工作表列映射
 */
export function healLitigationClaimColumnMappings(fields: LitigationPlanFieldMapping[]): LitigationPlanFieldMapping[] {
  return fields.map((field) => {
    if (field.enabled === false) return field;
    if (!CLAIM_COLUMN_HEADERS.has(compactLitigationHeader(field.header))) return field;
    if (field.sourceField === 'claimSummary') return field;
    return {...field, enabled: true, sourceField: 'claimSummary'};
  });
}

/**
 * 「诉讼与执行关联案号」只填一个关联案号，不能沿用案号的多层输入。
 * 表头已经是这一列、却仍映射到案号时，改到独立字段。
 * @param fields 工作表列映射
 */
export function healLitigationRelatedCourtNoColumnMappings(fields: LitigationPlanFieldMapping[]): LitigationPlanFieldMapping[] {
  return fields.map((field) => {
    if (field.enabled === false) return field;
    const header = compactLitigationHeader(field.header);
    if (!RELATED_COURT_NO_HEADERS.has(header)) return field;
    if (field.sourceField !== 'courtNo' && field.sourceField !== '') return field;
    return {...field, enabled: true, sourceField: 'relatedCourtNo'};
  });
}

/**
 * 在案件类型列之前插入「案件性质」。已有该列时不重复插入，只补上字段映射。
 * 插入点右侧的列位整体后移一列，与模板 Excel 插列保持一致。
 * @param fields 工作表列映射
 */
export function healLitigationCaseNatureColumnMappings(fields: LitigationPlanFieldMapping[]): LitigationPlanFieldMapping[] {
  const existing = fields.find((field) => field.sourceField === 'caseNature' || compactLitigationHeader(field.header) === '案件性质');
  if (existing) {
    return fields.map((field) => field === existing
      ? {...field, sourceField: 'caseNature', header: field.header?.trim() || '案件性质', enabled: field.enabled !== false}
      : field);
  }
  const disputeIndex = fields.findIndex((field) => field.sourceField === 'disputeType' || compactLitigationHeader(field.header) === '案件类型');
  const insertAt = disputeIndex >= 0 ? disputeIndex : fields.length;
  const columnIndex = disputeIndex >= 0
    ? fields[disputeIndex]!.columnIndex
    : fields.reduce((max, field) => Math.max(max, field.columnIndex), -1) + 1;
  const shifted = fields.map((field) => {
    if (field.columnIndex < columnIndex) return field;
    const nextIndex = field.columnIndex + 1;
    return {...field, columnIndex: nextIndex, columnLetter: columnLetter(nextIndex)};
  });
  shifted.splice(insertAt, 0, {
    enabled: true,
    columnIndex,
    columnLetter: columnLetter(columnIndex),
    header: '案件性质',
    sourceField: 'caseNature',
    valueMode: 'source',
  });
  return shifted;
}

/**
 * 在填写人列之前插入「所属单位」。已有该列时不重复插入，只补上字段映射。
 * 插入点右侧的列位整体后移一列，与模板 Excel 插列保持一致。
 * @param fields 工作表列映射
 */
export function healLitigationOrganizationUnitColumnMappings(fields: LitigationPlanFieldMapping[]): LitigationPlanFieldMapping[] {
  const existing = fields.find((field) => field.sourceField === 'organizationUnit' || compactLitigationHeader(field.header) === '所属单位');
  if (existing) {
    return fields.map((field) => field === existing
      ? {...field, sourceField: 'organizationUnit', header: field.header?.trim() || '所属单位', enabled: field.enabled !== false}
      : field);
  }
  const ownerIndex = fields.findIndex((field) => field.sourceField === 'ownerName' || compactLitigationHeader(field.header) === '填写人');
  const insertAt = ownerIndex >= 0 ? ownerIndex : fields.length;
  const columnIndex = ownerIndex >= 0
    ? fields[ownerIndex]!.columnIndex
    : fields.reduce((max, field) => Math.max(max, field.columnIndex), -1) + 1;
  const shifted = fields.map((field) => {
    if (field.columnIndex < columnIndex) return field;
    const nextIndex = field.columnIndex + 1;
    return {...field, columnIndex: nextIndex, columnLetter: columnLetter(nextIndex)};
  });
  shifted.splice(insertAt, 0, {
    enabled: true,
    columnIndex,
    columnLetter: columnLetter(columnIndex),
    header: '所属单位',
    sourceField: 'organizationUnit',
    valueMode: 'source',
  });
  return shifted;
}

export function healLitigationCaseNameColumnMappings(fields: LitigationPlanFieldMapping[]): LitigationPlanFieldMapping[] {
  const caseNameKey = normalizeKey('案件名称');
  const disputeNameKey = normalizeKey('纠纷名称');
  return fields.map((field) => {
    if (field.sourceField !== 'title') return field;
    const key = normalizeKey(String(field.header ?? ''));
    if (key === caseNameKey || key === disputeNameKey) {
      return {...field, sourceField: 'caseName'};
    }
    return field;
  });
}

/**
 * 根据表头推荐标准字段。
 * @param header 表头文本
 * @param sheetKind 当前工作表类型
 */
export function suggestLitigationPlanField(header: string, sheetKind: ReturnType<typeof detectLitigationSheetKind> = 'cases'): string {
  const parts = [header, ...header.split(/[\/|]/)].map((item) => item.trim()).filter(Boolean);
  let best = '';
  let bestScore = 0;
  for (const part of parts) {
    const key = normalizeKey(part);
    if (!key) continue;
    const sheetFieldIds = new Set(LITIGATION_DEFAULT_SHEETS.find((item) => item.kind === sheetKind)?.fieldIds ?? []);
    const pool = sheetKind === 'cases'
      ? LITIGATION_PLAN_STANDARD_FIELDS
      : [
        ...LITIGATION_DETAIL_FIELDS.filter((item) => item.sheetKind === sheetKind),
        ...LITIGATION_PLAN_STANDARD_FIELDS.filter((item) => sheetFieldIds.has(item.id)),
      ];
    for (const field of pool) {
      for (const alias of [field.label, ...field.aliases]) {
        const aliasKey = normalizeKey(alias);
        if (!aliasKey) continue;
        let score = 0;
        if (key === aliasKey) score = 100;
        else if (field.id !== 'organizationUnit' && (key.includes(aliasKey) || aliasKey.includes(key))) score = Math.max(aliasKey.length, key.length);
        if (score > bestScore) {
          bestScore = score;
          best = field.id;
        }
      }
    }
  }
  return bestScore >= 4 ? best : '';
}

/**
 * 案件上要展示的分层案号。只有一格旧案号时，按案件类型放进对应层次。
 * @param item 案件
 */
export function resolveLitigationCourtNumbers(
  item: Pick<LitigationCase, 'courtNo' | 'courtNumbers' | 'disputeType' | 'instance' | 'enforcementStatus'>,
): LitigationCourtNumbers {
  const stored = item.courtNumbers ? normalizeLitigationCourtNumbers(item.courtNumbers) : emptyLitigationCourtNumbers();
  if (litigationCourtNumberSummary(stored)) return stored;
  const text = String(item.courtNo || '').trim().slice(0, 80);
  if (!text) return stored;
  const kind = deriveLitigationNumberingKind({
    disputeType: item.disputeType,
    instance: item.instance,
    enforcementStatus: item.enforcementStatus,
  });
  if (kind === 'zc') stored.arbitration = text;
  else if (kind === 'ldzc') stored.labor = text;
  else if (item.instance === 'enforcement') stored.enforcement = text;
  else if (item.instance === 'second') stored.second = text;
  else if (item.instance === 'retrial') stored.retrial = text;
  else stored.first = text;
  return stored;
}

/**
 * 编辑案号时按行展示。已保存的行优先；旧的单格案号归入对应层次。
 * @param item 案件
 */
export function resolveLitigationCourtNumberRows(
  item: Pick<LitigationCase, 'courtNo' | 'courtNumbers' | 'disputeType' | 'instance' | 'enforcementStatus'>,
): LitigationCourtNumberRow[] {
  const disputeType = item.disputeType || 'litigation';
  const allowed = litigationCourtNumberLayersForDispute({
    disputeType: item.disputeType,
    instance: item.instance,
    enforcementStatus: item.enforcementStatus,
  });
  const stored = Array.isArray(item.courtNumbers) || item.courtNumbers
    ? normalizeLitigationCourtNumberRows(item.courtNumbers)
    : [];
  const collapsed = stored.length === 1 && /[；;]/.test(stored[0]?.value || '');
  let rows: LitigationCourtNumberRow[];
  if (stored.length && !collapsed) rows = stored;
  else {
    const text = collapsed ? stored[0].value : String(item.courtNo || '');
    rows = /[；;]/.test(text)
      ? splitLitigationCourtNumberCell(text, disputeType)
      : (stored.length ? stored : normalizeLitigationCourtNumberRows(resolveLitigationCourtNumbers({...item, courtNumbers: undefined})));
  }
  const aligned = alignLitigationCourtNumberRows(rows, item);
  if (allowed.length >= 2) return aligned;
  const value = aligned.map((row) => row.value.trim()).filter(Boolean).join('；').slice(0, 80);
  const fallback = allowed[0]?.id || 'first';
  return value ? [{layer: fallback, value}] : [];
}

/**
 * 列表和导出里要显示的审级。
 * 诉讼案件默认一审；二审或再审案号那一格一旦打开，就改成对应审级，格子可以仍是空的。
 * 案件类型还没选定，或这一行是执行、仲裁、劳动仲裁时，不套用这个默认。
 * @param item 案件
 */
export function litigationShownInstance(item: Pick<LitigationCase, 'disputeType' | 'instance' | 'enforcementStatus' | 'courtNo' | 'courtNumbers' | 'unfilledFields'>): LitigationInstance | '' {
  const kind = deriveLitigationNumberingKind({
    disputeType: item.disputeType,
    instance: item.instance,
    enforcementStatus: item.enforcementStatus,
  });
  if (kind === 'zx') return 'enforcement';
  if (kind !== 'ss' || item.unfilledFields?.includes('disputeType')) {
    return item.unfilledFields?.includes('instance') ? '' : item.instance;
  }
  return litigationInstanceFromCourtRows(resolveLitigationCourtNumberRows(item)) || 'first';
}

/**
 * 原告、被告格子当前要分成的层次。
 * @param item 案件
 */
function partyLayersForItem(item: Pick<LitigationCase, 'disputeType' | 'instance' | 'enforcementStatus' | 'courtNo' | 'courtNumbers' | 'unfilledFields'>): LitigationPartyLayer[] {
  const shown = litigationShownInstance(item);
  return litigationPartyLayersForCase({
    disputeType: item.unfilledFields?.includes('disputeType') ? undefined : item.disputeType,
    instance: shown,
  });
}

/**
 * 读取案件字段值（供列表与导出）。
 * @param item 案件
 * @param fieldId 标准字段 ID
 */
export function litigationPlanFieldValue(item: LitigationCase, fieldId: string): string {
  if (fieldId === 'instance') {
    const shown = litigationShownInstance(item);
    return shown ? INSTANCE_LABELS[shown] : '';
  }
  if (item.unfilledFields?.includes(fieldId)) return '';
  switch (fieldId) {
    case 'ownerName': return item.ownerName;
    case 'organizationUnit': return item.organizationUnit || '';
    case 'seqNo': return item.rowOrder > 0 ? String(item.rowOrder) : '';
    case 'internalNo': return item.internalNo;
    case 'courtNo': return litigationCourtNumberSummary(resolveLitigationCourtNumberRows(item));
    case 'relatedCourtNo': return String(item.relatedCourtNo || '');
    case 'caseName': return item.caseName;
    case 'caseBackground': return item.caseBackground || '';
    case 'title': return item.title;
    case 'caseNature': return item.caseNature ? LITIGATION_CASE_NATURE_LABELS[item.caseNature] : '';
    case 'disputeType': {
      const kind = deriveLitigationNumberingKind({
        disputeType: item.disputeType,
        instance: item.instance,
        enforcementStatus: item.enforcementStatus,
      });
      return LITIGATION_NUMBERING_KIND_LABELS[kind];
    }
    case 'position': return LITIGATION_POSITION_CHOICE_LABELS[item.position] ?? POSITION_LABELS[item.position];
    case 'ourParty': return ourPartyName(item);
    case 'plaintiffParty': return formatPartyLayerCell(item.parties, 'plaintiff', partyLayersForItem(item));
    case 'opponent': return formatPartyLayerCell(item.parties, 'opponent', partyLayersForItem(item));
    case 'thirdParty': return partyNameByRole(item.parties, 'third');
    case 'venue': return litigationVenueSummary(resolveLitigationVenueRows(item)) || item.venue;
    case 'filingDate': return item.filingDate;
    case 'amount': return item.amount ? `${item.amount} ${item.currency || 'CNY'}` : '';
    case 'currentStage': return currentStageName(item);
    case 'closeMode': return item.closeMode ? CLOSE_MODE_LABELS[item.closeMode] : '';
    case 'enforcementStatus': return ENFORCEMENT_STATUS_LABELS[item.enforcementStatus] ?? '';
    case 'latestProgress': return latestLitigationProgress(item);
    case 'nextDeadline': return nextDeadlineDate(item);
    case 'handler': return item.handler;
    case 'counsel': return item.counsel;
    // 兼容旧数据：counselOrg 之前的属性名是 counselContact。
    // 用 || 而非 ??：新建案例的 counselOrg 是空串，此时必须回退到旧字段。
    case 'counselOrg': return item.counselOrg || (item as {counselContact?: string}).counselContact || '';
    case 'claimSummary': return formatLitigationClaimCell(item);
    case 'resultSummary': return formatLitigationResultCell(item);
    case 'awardedAmount': return formatLitigationAwardedAmountCell(item);
    case 'nonMonetaryResult': return item.nonMonetaryResult ?? '';
    case 'closedFlag': return item.closed ? '是' : '否';
    case 'involvedAmount': return formatLitigationInvolvedAmountCell(item);
    case 'closedAt': return item.closedAt.slice(0, 10);
    case 'id': return item.id;
    default: return '';
  }
}

function labelKey(value: string): string {
  return value.replace(/\s+/g, '').replace(/[：:（）()·]/g, '');
}

/**
 * 将 Excel 单元格写回标准案件字段。未识别的值保持原样。
 * @param item 案件
 * @param fieldId 标准字段
 * @param raw 单元格文本
 */
export function applyLitigationPlanFieldValue(item: LitigationCase, fieldId: string, raw: string): void {
  const value = raw.trim();
  if (fieldId === 'id' && value) item.id = value.slice(0, 80);
  if (fieldId === 'ownerName') item.ownerName = value.slice(0, 160);
  if (fieldId === 'organizationUnit') item.organizationUnit = value.slice(0, 200);
  if (fieldId === 'internalNo') item.internalNo = value.slice(0, LITIGATION_INTERNAL_NO_MAX_LENGTH);
  if (fieldId === 'courtNo') {
    const rows = splitLitigationCourtNumberCell(value, item.disputeType || 'litigation');
    item.courtNumbers = rows;
    item.courtNo = litigationCourtNumberSummary(rows).slice(0, 400);
  }
  if (fieldId === 'relatedCourtNo') item.relatedCourtNo = value.slice(0, 80);
  if (fieldId === 'caseName') item.caseName = value.slice(0, 200);
  if (fieldId === 'caseBackground') item.caseBackground = value.slice(0, 4000);
  if (fieldId === 'title') item.title = value.slice(0, 200);
  if (fieldId === 'caseNature') item.caseNature = parseLitigationCaseNature(value);
  if (fieldId === 'venue') {
    const rows = splitLitigationVenueCell(value, item.disputeType || 'litigation');
    item.venues = rows;
    item.venue = litigationVenueSummary(rows).slice(0, 200);
  }
  if (fieldId === 'filingDate') item.filingDate = value.slice(0, 10);
  if (fieldId === 'handler') item.handler = value.slice(0, 80);
  if (fieldId === 'counsel') item.counsel = value.slice(0, 120);
  if (fieldId === 'counselOrg') item.counselOrg = value.slice(0, 160);
  if (fieldId === 'claimSummary') {
    const parts = splitLitigationClaimCell(value);
    item.claimSummary = parts.claim;
    item.counterclaimSummary = parts.counterclaim;
  }
  if (fieldId === 'resultSummary') {
    const parts = splitLitigationResultCell(value);
    item.resultSummary = parts.primary;
    item.counterclaimResult = parts.counter;
  }
  if (fieldId === 'awardedAmount') {
    const parts = splitLitigationAwardedAmountCell(value);
    item.awardedAmount = parts.primary;
    item.counterclaimAwardedAmount = parts.counter;
  }
  if (fieldId === 'nonMonetaryResult') item.nonMonetaryResult = value.slice(0, 2000);
  if (fieldId === 'closedFlag') {
    // 旧列仍可能读到是/否。空值不改动。结案现在由当前阶段决定，这一列已改为案涉金额。
    const key = labelKey(value);
    if (key) item.closed = key === '是' || key === 'true' || key === 'y' || key === 'yes' || key === '1';
  }
  if (fieldId === 'involvedAmount') {
    const key = labelKey(value);
    if (key === '是' || key === '否' || key === 'true' || key === 'false' || key === 'yes' || key === 'no' || key === 'y' || key === 'n') {
      item.closed = key === '是' || key === 'true' || key === 'y' || key === 'yes';
    } else if (value) {
      const parts = splitLitigationInvolvedAmountCell(value);
      item.principalAmount = parts.principal;
      item.counterclaimAmount = parts.counterclaim;
    }
  }
  if (fieldId === 'currentStage') {
    const name = value.trim();
    if (name === '已结案' || name === '结案' || name === '诉讼结案') item.closed = true;
    else if (name) {
      item.closed = false;
      item.litigationClosedAt = '';
      item.nodes = applyStageToNodes(alignProcedureNodesForStage(item.nodes || [], item.disputeType, name), name, '');
    }
  }
  if (fieldId === 'closedAt' && value) {
    const text = value.slice(0, 10);
    item.closedAt = text;
    /**
     * 结案日里的「已结案」「诉讼结案」不是日期，不能据此把当前阶段锁成已结案。
     */
    if (item.closed && /^\d{4}-\d{2}-\d{2}$/.test(text)) item.litigationClosedAt = item.litigationClosedAt || text;
  }
  if (fieldId === 'ourParty' && value) item.ourEntity = value.slice(0, 200);
  if (fieldId === 'plaintiffParty' || (fieldId === 'opponent' && value)) {
    const role = fieldId === 'plaintiffParty' ? 'plaintiff' : 'opponent';
    const parsed = value ? parsePartyLayerCell(value, role, item.disputeType) : null;
    item.parties = parsed
      ? replacePartyRoleLayers(item.parties, role, parsed, `party_${item.id || 'row'}`)
      : upsertPartyRoleName(item.parties, role, value, `party_${role}_${item.id || 'row'}`);
  }
  if (fieldId === 'thirdParty') item.parties = upsertPartyRoleName(item.parties, 'third', value, `party_third_${item.id || 'row'}`);
  if (fieldId === 'amount' && value) {
    const matched = value.replace(/,/g, '').match(/[\d.]+/);
    item.amount = matched ? Math.max(0, Number(matched[0]) || 0) : 0;
    if (/\bUSD\b/i.test(value)) item.currency = 'USD';
  }
  if (fieldId === 'principalAmount' || fieldId === 'counterclaimAmount') {
    const parsed = parseLitigationMoney(value);
    if (parsed !== null) item[fieldId] = parsed;
  }
  if (fieldId === 'disputeType') {
    const kind = parseLitigationCaseTypeChoice(value);
    if (kind) {
      const seeded = caseFieldsFromNumberingKind(kind);
      item.disputeType = seeded.disputeType;
      item.instance = seeded.instance;
      item.enforcementStatus = seeded.enforcementStatus;
    }
  }
  if (fieldId === 'instance') {
    const parsed = parseLitigationInstanceChoice(value);
    if (parsed) {
      item.instance = parsed;
    } else {
      const key = labelKey(value);
      if (key.includes('执行')) item.instance = 'enforcement';
    }
  }
  if (fieldId === 'position') {
    const parsed = parseLitigationPositionChoice(value);
    if (parsed) item.position = parsed;
  }
  if (fieldId === 'closeMode') {
    item.closeMode = parseCloseMode(value);
  }
  if (fieldId === 'enforcementStatus') {
    const key = labelKey(value);
    item.enforcementStatus = key.includes('完毕') ? 'completed' : key.includes('终本') ? 'terminated' : key.includes('执行中') || key === '执行' ? 'in_progress' : 'none';
  }
}

/**
 * 从案件总表方案列（启用且已映射的标准字段）推导 Web 列表列，与 Excel 方案一致。
 * @param plan 方案中的导出列或 sheetMaps
 */
export function deriveLitigationListFieldIdsFromPlan(plan: {
  listFieldIds?: string[];
  exportFields?: LitigationPlanFieldMapping[];
  sheetMaps?: Array<{sheetKind: LitigationSheetKind; exportFields: LitigationPlanFieldMapping[]}>;
}): string[] {
  const casesMap = plan.sheetMaps?.find((item) => item.sheetKind === 'cases');
  const mappings = (casesMap?.exportFields?.length ? casesMap.exportFields : plan.exportFields) ?? [];
  const ordered = [...mappings]
    .filter((item) => item.enabled !== false && item.sourceField && fieldById.has(item.sourceField))
    .map((item) => item.sourceField);
  const unique: string[] = [];
  for (const id of ordered) {
    if (id && !unique.includes(id)) unique.push(id);
  }
  if (unique.length) return unique;
  return resolveLitigationListFieldIds(plan.listFieldIds);
}

/** 客户端按后台保存的行序展示，保留重复映射及未映射列，不能按字段 ID 去重。 */
export type LitigationDisplayColumn = {id: string; sourceField: string; label: string; columnIndex: number};

export function litigationDisplayColumnsForSheet(
  plan: Pick<LitigationLedgerPlan, 'sheetMaps' | 'exportFields'>,
  kind: LitigationSheetKind,
): LitigationDisplayColumn[] {
  const map = plan.sheetMaps?.find((item) => item.sheetKind === kind);
  const rawFields = map?.exportFields ?? (kind === 'cases' ? plan.exportFields : []);
  const fields = kind === 'cases'
    ? healLitigationAwardedAmountColumnMappings(healLitigationResultColumnMappings(healLitigationInvolvedAmountColumnMappings(healLitigationClaimColumnMappings(healLitigationFilingDateColumnMappings(healLitigationRelatedCourtNoColumnMappings(healLitigationOurPartyColumnMappings(rawFields)))))))
    : rawFields;
  const seen = new Set<string>();
  return [...fields].filter((field) => field.enabled !== false)
    .map((field) => {
      const sourceField = field.sourceField || '';
      const id = sourceField && !seen.has(sourceField) ? sourceField : `template_${kind}_${field.columnIndex}`;
      seen.add(id);
      return {id, sourceField, label: field.header?.trim() || litigationPlanFieldLabel(sourceField), columnIndex: field.columnIndex};
    });
}

/**
 * 解析列表列 ID，忽略未知字段。
 * 没有配置时返回空数组，不再用内置默认列顶上，避免画出最早那套字段。
 * @param fieldIds 方案配置的列
 */
export function resolveLitigationListFieldIds(fieldIds: string[] | undefined): string[] {
  return (fieldIds ?? []).filter((id) => fieldById.has(id));
}

/**
 * 字段标签。
 * @param fieldId 标准字段 ID
 */
export function litigationPlanFieldLabel(fieldId: string): string {
  return fieldById.get(fieldId)?.label ?? fieldId;
}

/**
 * 指定工作表类型下可被跨表引用的标准字段。
 * @param sheetKind 工作表类型
 */
export function litigationReferableFieldsForSheetKind(
  sheetKind: LitigationSheetKind,
): Array<{id: string; label: string}> {
  return litigationFieldsForSheetKind(sheetKind, LITIGATION_PLAN_STANDARD_FIELDS).map((item) => ({
    id: item.id,
    label: item.label,
  }));
}

/**
 * 「最新进展」列默认引用进展记录表「内容」字段。
 */
export function defaultLatestProgressReference(): Pick<
  LitigationPlanFieldMapping,
  'valueMode' | 'referenceSheetName' | 'referenceHeader' | 'sourceModuleId' | 'sourceModuleVersion'
> {
  const progressSheet = LITIGATION_DEFAULT_SHEETS.find((item) => item.kind === 'progress');
  const content = LITIGATION_DETAIL_FIELDS.find((item) => item.id === 'progressContent');
  return {
    valueMode: 'reference',
    referenceSheetName: progressSheet?.name || '进展记录',
    referenceHeader: content?.label || '内容',
    sourceModuleId: LITIGATION_SHEET_REFERENCE_MODULE,
    sourceModuleVersion: '1',
  };
}

/**
 * 由引用表头解析被引用的标准字段 ID。
 * @param header 引用列表头
 * @param sheetKind 被引用工作表类型
 */
export function litigationReferenceSourceFieldId(header: string, sheetKind: LitigationSheetKind): string {
  return suggestLitigationPlanField(header, sheetKind) || '';
}

/**
 * 按方案生成导出行（表头 + 数据）。
 * @param plan 方案
 * @param cases 可见案件
 */
export function buildLitigationPlanExportRows(plan: LitigationLedgerPlan, cases: LitigationCase[]): string[][] {
  const mappings = (plan.exportFields ?? []).filter((item) => item.enabled && item.sourceField);
  const active = mappings.length ? mappings : defaultLitigationExportFields();
  const header = active.map((item) => item.header.trim() || litigationPlanFieldLabel(item.sourceField));
  const rows = cases.map((item) => active.map((mapping) => litigationPlanFieldValue(item, mapping.sourceField)));
  return [header, ...rows];
}

/**
 * 按方案选择程序节点模板。
 * @param plan 方案
 * @param disputeType 争议类型
 */
export function procedureNodesForPlan(
  plan: LitigationLedgerPlan | null | undefined,
  disputeType: LitigationDisputeType,
): LitigationProcedureNode[] {
  const override = plan?.procedureTemplates?.[disputeType];
  if (override?.length) {
    return override.map((node) => ({
      ...node,
      plannedDate: '',
      completedDate: '',
      note: '',
    }));
  }
  return procedureTemplateFor(disputeType);
}

export type InspectedLitigationTemplateColumn = {
  columnIndex: number;
  columnLetter: string;
  header: string;
  suggestedField: string;
  suggestedScore: number;
};

/**
 * 把识别到的列原样转成导出映射：保留 Excel 里的真实表头与真实列位。
 *
 * 与 {@link mergeLitigationSheetExportFieldsWithCatalog} 的区别：
 * 后者会把映射「补全为系统标准字段目录」，未识别的列会被丢掉；
 * 本函数只忠实反映用户上传的 Excel 结构，未识别的列同样保留（sourceField 为空），
 * 这样平台配置与用户的表格结构始终一致。
 *
 * @param columns 识别到的列
 */
export function litigationExportFieldsFromInspectedColumns(
  columns: readonly InspectedLitigationTemplateColumn[],
): LitigationPlanFieldMapping[] {
  return columns.map((column) => {
    const mapping = normalizeLitigationPlanFieldMapping({
      enabled: Boolean(column.suggestedField),
      columnIndex: column.columnIndex,
      columnLetter: column.columnLetter,
      header: column.header,
      sourceField: column.suggestedField,
      valueMode: 'source',
    });
    return mapping ?? {
      enabled: false,
      columnIndex: column.columnIndex,
      columnLetter: column.columnLetter,
      header: column.header,
      sourceField: '',
      valueMode: 'source' as const,
    };
  });
}

export type InspectedLitigationTemplateSheet = {
  name: string;
  suggestedHeaderRow: number;
  rowCount: number;
  sheetKind?: 'cases' | 'parties' | 'procedure' | 'progress';
  columns: InspectedLitigationTemplateColumn[];
};
